import React, { useState, useMemo } from 'react';
import {
    Task, ProtocolState, ProtocolConfig, WeeklyPlan, PhysicalUnit, Subcontractor, Contract,
    PaymentStatement, CompanyConfig, ProjectInfo, Responsible, StatementItem, UnitDetail,
    SubcontractType, Level, AnexoContrato
} from '../src/types';
import { truncateProgress, findTaskById } from '../src/utils/planningUtils';

interface PaymentsScreenProps {
    tasks: Task[];
    units: PhysicalUnit[];
    levels: Level[];
    matrixData: Record<string, string>;
    protocolConfigs: Record<string, ProtocolConfig>; // New: required for payment flags
    subcontractors: Subcontractor[];
    setSubcontractors: React.Dispatch<React.SetStateAction<Subcontractor[]>>;
    contracts: Contract[];
    setContracts: React.Dispatch<React.SetStateAction<Contract[]>>;
    paymentStatements: PaymentStatement[];
    setPaymentStatements: React.Dispatch<React.SetStateAction<PaymentStatement[]>>;
    savedProtocols: Record<string, ProtocolState>;
    subcontractTypes: SubcontractType[];
    setSubcontractTypes: React.Dispatch<React.SetStateAction<SubcontractType[]>>;
    projectInfo: { name: string };
    addActivity: (activity: any) => void;
    companyConfig: CompanyConfig;
    hasPermission: (module: string, action: 'view'|'edit') => boolean;
    isPlatformAdmin?: boolean;
    globalUsers?: any[];
    responsibles?: Responsible[];
}

const PaymentsScreen: React.FC<PaymentsScreenProps> = ({
    tasks,
    units,
    levels,
    matrixData,
    protocolConfigs,
    subcontractors,
    setSubcontractors,
    contracts,
    setContracts,
    paymentStatements,
    setPaymentStatements,
    savedProtocols,
    subcontractTypes,
    setSubcontractTypes,
    projectInfo,
    addActivity,
    companyConfig,
    hasPermission,
    isPlatformAdmin,
    globalUsers = [],
    responsibles = []
}) => {
    // --- State ---
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    const [menuOpen, setMenuOpen] = useState(false);

    // Modals visibility
    const [createMasterSubModalOpen, setCreateMasterSubModalOpen] = useState(false);
    const [listMasterSubModalOpen, setListMasterSubModalOpen] = useState(false);

    // Main Form States (Old Create Modal - kept for "Añadir")
    const [addStatementModalOpen, setAddStatementModalOpen] = useState(false);
    const [anexosListModalOpen, setAnexosListModalOpen] = useState(false);
    const [anexoFormModalOpen, setAnexoFormModalOpen] = useState(false);
    const [subcontractorPickerOpen, setSubcontractorPickerOpen] = useState(false);
    const [viewEPModalOpen, setViewEPModalOpen] = useState(false); // New State for "Ver EP"
    const [isAnexoMode, setIsAnexoMode] = useState(false);

    const [currentAnexoForm, setCurrentAnexoForm] = useState<Partial<AnexoContrato>>({
        id: '',
        numero: '',
        fecha: new Date().toISOString().split('T')[0],
        description: '',
        items: [],
        totalAmount: 0,
        status: 'Borrador'
    });

    // NEW STATES for "Generar EP" Workflow
    const [generateEpOpen, setGenerateEpOpen] = useState(false);
    const [detailEpOpen, setDetailEpOpen] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);

    // NEW: EP Detail Modal (Cover View)
    const [selectedEPForView, setSelectedEPForView] = useState<PaymentStatement | null>(null);
    const [epDetailModalOpen, setEpDetailModalOpen] = useState(false);
    const [expandedItemsInView, setExpandedItemsInView] = useState<Set<string>>(new Set());
    const [retentionPercentageInView, setRetentionPercentageInView] = useState<number>(0);

    const [currentDraftEp, setCurrentDraftEp] = useState<Partial<PaymentStatement>>({});
    const [activeDraftTask, setActiveDraftTask] = useState<{ id: string, name: string, code: string } | null>(null);
    const [tempSelectedDetailUnits, setTempSelectedDetailUnits] = useState<string[]>([]); // Units selected for payment in detail view

    // Item Selection States
    const [taskSelectorOpen, setTaskSelectorOpen] = useState(false); // Step 1: Pick Task
    const [unitSelectorOpen, setUnitSelectorOpen] = useState(false); // Step 2: Pick Units
    const [selectedTaskForAdd, setSelectedTaskForAdd] = useState<{ id: string, code: string, name: string } | null>(null);
    const [tempSelectedUnits, setTempSelectedUnits] = useState<string[]>([]); // Array of unitIds

    // Documents Modal State
    const [documentsModalOpen, setDocumentsModalOpen] = useState(false);

    // NEW: Subcontract Type Management
    const [subcontractTypeModalOpen, setSubcontractTypeModalOpen] = useState(false);
    const [typeSearchQuery, setTypeSearchQuery] = useState('');
    const [newTypeName, setNewTypeName] = useState('');

    // Chile Regions and Communes Data
    const chileData = [
        {
            region: "Arica y Parinacota",
            comunas: ["Arica", "Camarones", "Putre", "General Lagos"]
        },
        {
            region: "Tarapacá",
            comunas: ["Iquique", "Alto Hospicio", "Pozo Almonte", "Camiña", "Colchane", "Huara", "Pica"]
        },
        {
            region: "Antofagasta",
            comunas: ["Antofagasta", "Mejillones", "Sierra Gorda", "Taltal", "Calama", "Ollagüe", "San Pedro de Atacama", "Tocopilla", "María Elena"]
        },
        {
            region: "Atacama",
            comunas: ["Copiapó", "Caldera", "Tierra Amarilla", "Chañaral", "Diego de Almagro", "Vallenar", "Alto del Carmen", "Freirina", "Huasco"]
        },
        {
            region: "Coquimbo",
            comunas: ["La Serena", "Coquimbo", "Andacollo", "La Higuera", "Paiguano", "Vicuña", "Illapel", "Canela", "Los Vilos", "Salamanca", "Ovalle", "Combarbalá", "Monte Patria", "Punitaqui", "Río Hurtado"]
        },
        {
            region: "Valparaíso",
            comunas: ["Valparaíso", "Casablanca", "Concón", "Juan Fernández", "Puchuncaví", "Quintero", "Viña del Mar", "Isla de Pascua", "Los Andes", "Calle Larga", "Rinconada", "San Esteban", "La Ligua", "Cabildo", "Papudo", "Petorca", "Zapallar", "Quillota", "Calera", "Hijuelas", "La Cruz", "Nogales", "San Antonio", "Algarrobo", "Cartagena", "El Quisco", "El Tabo", "Santo Domingo", "San Felipe", "Catemu", "Llaillay", "Panquehue", "Putaendo", "Santa María", "Quilpué", "Villa Alemana"]
        },
        {
            region: "Región del Libertador Gral. Bernardo O’Higgins",
            comunas: ["Rancagua", "Codegua", "Coinco", "Coltauco", "Doñihue", "Graneros", "Las Cabras", "Machalí", "Malloa", "Mostazal", "Olivar", "Peumo", "Pichidegua", "Quinta de Tilcoco", "Rengo", "Requínoa", "San Vicente", "Pichilemu", "La Estrella", "Litueche", "Marchihue", "Navidad", "Paredones", "San Fernando", "Chépica", "Chimbarongo", "Lolol", "Nancagua", "Palmilla", "Peralillo", "Placilla", "Pumanque", "Santa Cruz"]
        },
        {
            region: "Región del Maule",
            comunas: ["Talca", "Constitución", "Curepto", "Empedrado", "Maule", "Pelarco", "Pencahue", "Río Claro", "San Clemente", "San Rafael", "Cauquenes", "Chanco", "Pelluhue", "Curicó", "Hualañé", "Licantén", "Molina", "Rauco", "Romeral", "Sagrada Familia", "Teno", "Vichuquén", "Linares", "Colbún", "Longaví", "Parral", "Retiro", "San Javier", "Villa Alegre", "Yerbas Buenas"]
        },
        {
            region: "Región de Ñuble",
            comunas: ["Chillán", "Bulnes", "Cobquecura", "Coelemu", "Coihueco", "Chillán Viejo", "El Carmen", "Ninhue", "Ñiquén", "Pemuco", "Pinto", "Portezuelo", "Quillón", "Quirihue", "Ránquil", "San Carlos", "San Fabián", "San Ignacio", "San Nicolás", "Treguaco", "Yungay"]
        },
        {
            region: "Región del Biobío",
            comunas: ["Concepción", "Coronel", "Chiguayante", "Florida", "Hualpén", "Hualqui", "Lota", "Penco", "San Pedro de la Paz", "Santa Juana", "Talcahuano", "Tomé", "Lebu", "Arauco", "Cañete", "Contulmo", "Curanilahue", "Los Álamos", "Tirúa", "Los Ángeles", "Antuco", "Cabrero", "Laja", "Mulchén", "Nacimiento", "Negrete", "Quilaco", "Quilleco", "San Rosendo", "Santa Bárbara", "Tucapel", "Yumbel", "Alto Biobío"]
        },
        {
            region: "Región de la Araucanía",
            comunas: ["Temuco", "Carahue", "Cunco", "Curarrehue", "Freire", "Galvarino", "Gorbea", "Lautaro", "Loncoche", "Melipeuco", "Nueva Imperial", "Padre las Casas", "Perquenco", "Pitrufquén", "Pucón", "Saavedra", "Teodoro Schmidt", "Toltén", "Vilcún", "Villarrica", "Cholchol", "Angol", "Collipulli", "Curacautín", "Ercilla", "Lonquimay", "Los Sauces", "Lumaco", "Purén", "Renaico", "Traiguén", "Victoria"]
        },
        {
            region: "Región de los Ríos",
            comunas: ["Valdivia", "Corral", "Lanco", "Los Lagos", "Máfil", "Mariquina", "Paillaco", "Panguipulli", "La Unión", "Futrono", "Lago Ranco", "Río Bueno"]
        },
        {
            region: "Región de los Lagos",
            comunas: ["Puerto Montt", "Calbuco", "Cochamó", "Fresia", "Frutillar", "Los Muermos", "Llanquihue", "Maullín", "Puerto Varas", "Castro", "Ancud", "Chonchi", "Curaco de Vélez", "Dalcahue", "Puqueldón", "Queilén", "Quellón", "Quemchi", "Quinchao", "Osorno", "Puerto Octay", "Purranque", "Puyehue", "Río Negro", "San Juan de la Costa", "San Pablo", "Chaitén", "Futaleufú", "Hualaihué", "Palena"]
        },
        {
            region: "Región Aysén del Gral. Carlos Ibáñez del Campo",
            comunas: ["Coyhaique", "Lago Verde", "Aysén", "Cisnes", "Guaitecas", "Cochrane", "O’Higgins", "Tortel", "Chile Chico", "Río Ibáñez"]
        },
        {
            region: "Región de Magallanes y de la Antártica Chilena",
            comunas: ["Punta Arenas", "Laguna Blanca", "Río Verde", "San Gregorio", "Cabo de Hornos", "Antártica", "Porvenir", "Primavera", "Timaukel", "Natales", "Torres del Paine"]
        },
        {
            region: "Región Metropolitana de Santiago",
            comunas: ["Santiago", "Cerrillos", "Cerro Navia", "Conchalí", "El Bosque", "Estación Central", "Huechuraba", "Independencia", "La Cisterna", "La Florida", "La Granja", "La Pintana", "La Reina", "Las Condes", "Lo Barnechea", "Lo Espejo", "Lo Prado", "Macul", "Maipú", "Ñuñoa", "Pedro Aguirre Cerda", "Peñalolén", "Providencia", "Pudahuel", "Quilicura", "Quinta Normal", "Recoleta", "Renca", "San Joaquín", "San Miguel", "San Ramón", "Vitacura", "Puente Alto", "Pirque", "San José de Maipo", "San Bernardo", "Buin", "Calera de Tango", "Paine", "Melipilla", "Alhué", "Curacaví", "María Pinto", "San Pedro", "Talagante", "El Monte", "Isla de Maipo", "Padre Hurtado", "Peñaflor", "Colina", "Lampa", "Tiltil"]
        }
    ];

    // Initial selected statement to avoid null on first render if list is not empty
    const [selectedContractId, setSelectedContractId] = useState<string | null>(contracts.length > 0 ? contracts[0].id : null);

    // State for the "New/Edit Statement" Form (Now essentially "Edit Contract")
    const [currentForm, setCurrentForm] = useState({
        id: '', // Empty for new, populated for edit
        subcontractorId: '',
        rut: '',
        razonSocial: '',
        startDate: '',
        endDate: '',
        retention: 0,
        items: [] as StatementItem[],
        attachedDocuments: [] as string[],
        linkedUserIds: [] as string[]
    });

    // State for New Master Subcontractor
    const [newSubcontractor, setNewSubcontractor] = useState<Omit<Subcontractor, 'id'>>({
        rut: '',
        razonSocial: '',
        giro: '',
        representante: '',
        telefono: '',
        correo: '',
        subcontractTypeId: '',
        fono: '',
        ciudad: '',
        comuna: ''
    });

    // --- Actions ---

    // 1. Create Master Subcontractor
    const handleSaveMasterSubcontractor = () => {
        if (!newSubcontractor.rut || !newSubcontractor.razonSocial) {
            alert("Por favor ingrese al menos el RUT y la Razón Social.");
            return;
        }
        const newEntry: Subcontractor = {
            id: Math.random().toString(36).substr(2, 9),
            ...newSubcontractor
        };
        setSubcontractors([...subcontractors, newEntry]);
        setCreateMasterSubModalOpen(false);
        setNewSubcontractor({
            rut: '',
            razonSocial: '',
            giro: '',
            representante: '',
            telefono: '',
            correo: '',
            subcontractTypeId: '',
            fono: '',
            ciudad: '',
            comuna: ''
        });
        setListMasterSubModalOpen(true);
    };

    const handleCreateSubcontractType = () => {
        if (!newTypeName.trim()) return;
        const newType: SubcontractType = {
            id: 'st-' + Math.random().toString(36).substr(2, 5),
            name: newTypeName.trim()
        };
        setSubcontractTypes([...subcontractTypes, newType]);
        setNewTypeName('');
    };

    // 2. Open "Add Contract" Modal (CREATE MODE)
    const handleOpenAddStatement = () => {
        setCurrentForm({
            id: '',
            subcontractorId: '',
            rut: '',
            razonSocial: '',
            startDate: '',
            endDate: '',
            retention: 0,
            items: [],
            attachedDocuments: [],
            linkedUserIds: []
        });
        setAddStatementModalOpen(true);
    };

    // 2.5 Open "Edit Contract" Modal (EDIT MODE)
    const handleEditStatement = (contractId: string | null = selectedContractId) => {
        const stmt = contracts.find(p => p.id === contractId);
        if (!stmt) return;

        const sub = getSubcontractorById(stmt.subcontractorId);

        setCurrentForm({
            id: stmt.id,
            subcontractorId: stmt.subcontractorId,
            rut: sub?.rut || '',
            razonSocial: sub?.razonSocial || '',
            startDate: stmt.startDate || '',
            endDate: stmt.endDate || '',
            retention: stmt.retention || 0,
            items: stmt.items ? JSON.parse(JSON.stringify(stmt.items)) : [], // Deep copy
            attachedDocuments: (stmt as any).attachedDocuments ? [...(stmt as any).attachedDocuments] : ((stmt as any).signedContractUrl ? [(stmt as any).signedContractUrl] : []),
            linkedUserIds: sub?.linkedUserIds || []
        });
        setAddStatementModalOpen(true);
    };

    // 3. Pick Subcontractor logic
    const handlePickSubcontractor = (sub: Subcontractor) => {
        setCurrentForm(prev => ({
            ...prev,
            subcontractorId: sub.id,
            rut: sub.rut,
            razonSocial: sub.razonSocial,
            linkedUserIds: sub.linkedUserIds || []
        }));
        setSubcontractorPickerOpen(false);
    };

    // 4. Selection Logic Flow
    const handleOpenTaskSelector = (forAnexo: boolean = false) => {
        setIsAnexoMode(forAnexo === true);
        setTaskSelectorOpen(true);
    };

    const handleTaskSelect = (task: Task, indexCode: string) => {
        setSelectedTaskForAdd({ id: task.id, code: indexCode, name: task.name });
        setTaskSelectorOpen(false);
        // Load existing selection if editing
        const existingItem = isAnexoMode
            ? currentAnexoForm.items?.find((i: StatementItem) => i.id === task.id)
            : currentForm.items.find((i: StatementItem) => i.id === task.id);
        
        if (existingItem) {
            setTempSelectedUnits(existingItem.details.map((d: UnitDetail) => d.unitId));
        } else {
            setTempSelectedUnits([]);
        }
        setUnitSelectorOpen(true);
    };

    // NEW LOGIC FOR GENERATE EP
    const handleGenerateEPClick = () => {
        // Find the selected contract to use as base
        const sourceContract = contracts.find(p => p.id === selectedContractId);

        if (!sourceContract) {
            alert("Seleccione un subcontrato de la lista para usar como base.");
            return;
        }

        if (!sourceContract.items || sourceContract.items.length === 0) {
            alert("El subcontrato seleccionado no tiene partidas asignadas. Agregue partidas mediante 'Editar' antes de generar un EP.");
            return;
        }

        // Consolidate base items + active anexos
        const consolidatedItemsMap = new Map<string, StatementItem>();
        
        // 1. Add base contract items
        sourceContract.items.forEach(item => {
            consolidatedItemsMap.set(item.id, JSON.parse(JSON.stringify(item)));
        });
        
        // 2. Add anexo items
        if (sourceContract.anexos) {
            sourceContract.anexos.filter(a => a.status === 'Activo').forEach(anexo => {
                anexo.items.forEach(aItem => {
                    if (aItem.totalValue < 0) return; // Skip eliminations, they were already removed from base contract items during authorization
                    
                    if (consolidatedItemsMap.has(aItem.id)) {
                        const existingItem = consolidatedItemsMap.get(aItem.id)!;
                        existingItem.totalValue += aItem.totalValue;
                        // Merge details
                        aItem.details.forEach(aDetail => {
                            const existingDetail = existingItem.details.find(d => d.unitId === aDetail.unitId);
                            if (existingDetail) {
                                existingDetail.value += aDetail.value;
                                existingDetail.quantity = ((parseFloat(existingDetail.quantity || '0') || 0) + (parseFloat(aDetail.quantity || '0') || 0)).toString();
                            } else {
                                existingItem.details.push(JSON.parse(JSON.stringify(aDetail)));
                            }
                        });
                    } else {
                        consolidatedItemsMap.set(aItem.id, JSON.parse(JSON.stringify(aItem)));
                    }
                });
            });
        }

        const draftItems: StatementItem[] = Array.from(consolidatedItemsMap.values()).map(item => ({
            ...item,
            paidAmount: 0,
            selectedUnitIds: [], // Start with no units selected for payment in this new EP
            details: item.details.map(d => ({ ...d })) // Deep copy details (the scope)
        }));

        // Initialize Draft
        setCurrentDraftEp({
            id: `ep-${Date.now()}`,
            contractId: sourceContract.id,
            subcontractorId: sourceContract.subcontractorId,
            startDate: new Date().toISOString().split('T')[0],
            endDate: new Date().toISOString().split('T')[0],
            status: 'Borrador',
            items: draftItems
        });

        setGenerateEpOpen(true);
    };

    const handleOpenEpDetail = (task: StatementItem) => {
        setActiveDraftTask({ id: task.id, name: task.description, code: task.code });
        // Use the saved selection state in the draft, or empty if none
        setTempSelectedDetailUnits(task.selectedUnitIds || []);
        setDetailEpOpen(true);
    };

    const toggleUnitSelection = (unitId: string) => {
        setTempSelectedUnits(prev =>
            prev.includes(unitId) ? prev.filter(id => id !== unitId) : [...prev, unitId]
        );
    };

    const handleConfirmUnits = () => {
        if (!selectedTaskForAdd) return;

        const details: UnitDetail[] = [];
        let totalVal = 0;

        // Collect details for selected units
        const validUnits = getValidUnitsForTask(selectedTaskForAdd.id);
        
        tempSelectedUnits.forEach(uId => {
            const unitInfo = validUnits.find(v => v.id === uId);
            if (!unitInfo) return;

            details.push({
                unitId: uId,
                unitName: unitInfo.name,
                value: unitInfo.value,
                quantity: unitInfo.quantity,
                unitPrice: unitInfo.unitPrice,
            });
            totalVal += unitInfo.value;
        });

        const newItem: StatementItem = {
            id: selectedTaskForAdd.id,
            code: selectedTaskForAdd.code,
            description: selectedTaskForAdd.name,
            totalValue: totalVal, // Total contract value for selected units
            paidAmount: 0,
            details: details,
            expanded: false
        };

        // Add to the regular Add/Edit Contract or Anexo Form
        if (isAnexoMode) {
            setCurrentAnexoForm(prev => {
                const exists = prev.items?.find(i => i.id === newItem.id);
                if (exists) {
                    return { ...prev, items: prev.items?.map(i => i.id === newItem.id ? newItem : i) };
                }
                return { ...prev, items: [...(prev.items || []), newItem] };
            });
        } else {
            setCurrentForm(prev => {
                const exists = prev.items.find(i => i.id === newItem.id);
                if (exists) {
                    return { ...prev, items: prev.items.map(i => i.id === newItem.id ? newItem : i) };
                }
                return { ...prev, items: [...prev.items, newItem] };
            });
        }

        setUnitSelectorOpen(false);
        setTaskSelectorOpen(false);
        setSelectedTaskForAdd(null);
        setTempSelectedUnits([]);
    };

    // Logic for Detail EP Modal (Saving selected payable units)
    const handleSaveDetailEp = () => {
        if (!activeDraftTask || !currentDraftEp.items) return;

        // Find the item in draft
        const itemIndex = currentDraftEp.items.findIndex(i => i.id === activeDraftTask.id);
        if (itemIndex === -1) return;

        const item = currentDraftEp.items[itemIndex];

        let newPaidAmount = 0;

        // Sum up values of selected units based on current checkbox selection
        item.details.forEach(d => {
            if (tempSelectedDetailUnits.includes(d.unitId)) {
                newPaidAmount += d.value;
            }
        });

        // Update Draft with new amount AND save the selection state
        const updatedItem = {
            ...item,
            paidAmount: newPaidAmount,
            selectedUnitIds: tempSelectedDetailUnits
        };

        const newItems = [...currentDraftEp.items];
        newItems[itemIndex] = updatedItem;

        setCurrentDraftEp({ ...currentDraftEp, items: newItems });
        setDetailEpOpen(false);
    };

    const toggleDetailUnitSelection = (unitId: string, amount: number) => {
        if (!activeDraftTask) return;
        if (!isProtocolPayable(activeDraftTask.id, unitId)) {
            alert("No se puede seleccionar esta unidad: El protocolo de calidad aún no ha sido aprobado por el revisor correspondiente para pago.");
            return;
        }
        if (amount <= 0) return;
        setTempSelectedDetailUnits(prev =>
            prev.includes(unitId) ? prev.filter(id => id !== unitId) : [...prev, unitId]
        );
    };

    // Authorization
    const handleAuthorizeClick = () => {
        setSignatureModalOpen(true);
    };

    const handleConfirmSignature = () => {
        // Save the Draft EP as a real "Autorizado" EP
        const totalAmount = currentDraftEp.items?.reduce((acc, i) => acc + i.paidAmount, 0) || 0;

        const newEp: PaymentStatement = {
            id: currentDraftEp.id || 'err',
            contractId: currentDraftEp.contractId || '',
            subcontractorId: currentDraftEp.subcontractorId || '',
            period: 'Nuevo Periodo', // Could be dynamic
            progress: 50, // Dummy calc
            amount: `${totalAmount.toLocaleString()} CLP`,
            status: 'Autorizado',
            startDate: currentDraftEp.startDate,
            endDate: currentDraftEp.endDate,
            items: currentDraftEp.items
        };

        addActivity({
            type: 'subcontracts',
            title: 'Estado de Pago Autorizado',
            description: `Se ha autorizado un EP por ${newEp.amount} para el subcontratista ${getSubcontractorById(newEp.subcontractorId)?.razonSocial}`,
            user: 'Usuario'
        });

        setPaymentStatements([...paymentStatements, newEp]);
        setSignatureModalOpen(false);
        setGenerateEpOpen(false);
    };

    // Toggle expansion of rows in main table
    const toggleItemExpansion = (itemId: string, forAnexo = false) => {
        if (forAnexo) {
            setCurrentAnexoForm(prev => ({
                ...prev,
                items: prev.items?.map(item => item.id === itemId ? { ...item, expanded: !item.expanded } : item)
            }));
        } else {
            setCurrentForm(prev => ({
                ...prev,
                items: prev.items.map(item => item.id === itemId ? { ...item, expanded: !item.expanded } : item)
            }));
        }
    };

    // 5.5 Anexos Flow
    const handleCreateAnexo = () => {
        setCurrentAnexoForm({
            id: '',
            numero: `Anexo N°${(selectedContract?.anexos?.length || 0) + 1}`,
            fecha: new Date().toISOString().split('T')[0],
            description: '',
            items: [],
            totalAmount: 0,
            status: 'Borrador'
        });
        setAnexoFormModalOpen(true);
    };

    const handleEditAnexo = (anexo: AnexoContrato) => {
        setCurrentAnexoForm(JSON.parse(JSON.stringify(anexo)));
        setAnexoFormModalOpen(true);
    };

    const handleSaveAnexo = () => {
        if (!selectedContractId) return;
        
        const totalAmount = currentAnexoForm.items?.reduce((acc, i) => acc + i.totalValue, 0) || 0;
        
        setContracts(prev => prev.map(p => {
            if (p.id === selectedContractId) {
                const existingAnexos = p.anexos || [];
                let newAnexos;
                
                if (currentAnexoForm.id) {
                    newAnexos = existingAnexos.map(a => a.id === currentAnexoForm.id ? { ...currentAnexoForm, totalAmount } as AnexoContrato : a);
                } else {
                    const newAnexo: AnexoContrato = {
                        ...(currentAnexoForm as AnexoContrato),
                        id: `anx-${Math.floor(Math.random() * 10000)}`,
                        totalAmount
                    };
                    newAnexos = [...existingAnexos, newAnexo];
                }
                
                return { ...p, anexos: newAnexos };
            }
            return p;
        }));
        
        setAnexoFormModalOpen(false);
    };

    const handleAuthorizeAnexo = () => {
        if (!selectedContractId || !currentAnexoForm.id) return;
        
        const totalAmount = currentAnexoForm.items?.reduce((acc, i) => acc + i.totalValue, 0) || 0;
        
        setContracts(prev => prev.map(p => {
            if (p.id === selectedContractId) {
                const existingAnexos = p.anexos || [];
                const newAnexos = existingAnexos.map(a => a.id === currentAnexoForm.id ? { ...currentAnexoForm, totalAmount, status: 'Activo' } as AnexoContrato : a);
                
                // Handle Eliminations: If an item in the anexo has a negative value, it removes it from the base contract
                let updatedBaseItems = [...p.items];
                const eliminatedIds = currentAnexoForm.items?.filter(i => i.totalValue < 0).map(i => i.id) || [];
                
                if (eliminatedIds.length > 0) {
                    updatedBaseItems = updatedBaseItems.filter(item => !eliminatedIds.includes(item.id));
                }

                // Important: Update the base contract total amount
                const updatedBaseTotalAmount = updatedBaseItems.reduce((acc, i) => acc + i.totalValue, 0);
                
                return { ...p, items: updatedBaseItems, anexos: newAnexos, totalAmount: updatedBaseTotalAmount };
            }
            return p;
        }));
        
        setAnexoFormModalOpen(false);
        addActivity({
            type: 'subcontracts',
            title: 'Anexo Autorizado',
            description: `Se ha autorizado el ${currentAnexoForm.numero}. Los cambios han sido aplicados al contrato base.`,
            user: 'Usuario'
        });
    };

    // 5. Save the whole CONTRACT (Standard Form)
    const handleSaveContract = () => {
        if (!currentForm.subcontractorId) {
            alert("Debe seleccionar un Subcontratista (Razón Social)");
            return;
        }

        setSubcontractors(prev => prev.map(s => {
            if (s.id === currentForm.subcontractorId) {
                return {
                    ...s,
                    linkedUserIds: currentForm.linkedUserIds
                };
            }
            return s;
        }));

        const totalAmount = currentForm.items.reduce((acc, i) => acc + i.totalValue, 0);

        const isContractValid = Boolean(currentForm.subcontractorId && currentForm.items.length > 0 && currentForm.rut && currentForm.razonSocial && currentForm.startDate && currentForm.endDate && currentForm.retention !== undefined && ((currentForm as any).signedContractUrl || (currentForm.attachedDocuments && currentForm.attachedDocuments.length > 0)));
        const finalStatus = isContractValid ? 'Activo' : 'En Proceso';

        // Check if updating or creating
        if (currentForm.id) {
            // Update
            setContracts(prev => prev.map(p => {
                if (p.id === currentForm.id) {
                    return {
                        ...p,
                        subcontractorId: currentForm.subcontractorId,
                        startDate: currentForm.startDate,
                        endDate: currentForm.endDate,
                        retention: currentForm.retention,
                        items: currentForm.items,
                        totalAmount: totalAmount,
                        status: p.status === 'Inactivo' ? 'Inactivo' : finalStatus
                    };
                }
                return p;
            }));
        } else {
            // Create New
            const newContract: Contract = {
                id: `ctr-${Math.floor(Math.random() * 10000)}`,
                subcontractorId: currentForm.subcontractorId,
                startDate: currentForm.startDate,
                endDate: currentForm.endDate,
                retention: currentForm.retention,
                items: currentForm.items,
                totalAmount: totalAmount,
                status: finalStatus
            };
            setContracts([...contracts, newContract]);
            setSelectedContractId(newContract.id);
        }

        const subName = getSubcontractorById(currentForm.subcontractorId)?.razonSocial || 'Desconocido';
        addActivity({
            type: 'subcontracts',
            title: currentForm.id ? 'Subcontrato Actualizado' : 'Nuevo Subcontrato Creado',
            description: `Subcontrato para ${subName} - Monto Total: $${totalAmount.toLocaleString()}`,
            user: 'Usuario'
        });

        setAddStatementModalOpen(false);
    };

    // 6. Authorize the Contract (Changes Status)
    const handleAuthorizeContract = () => {
        if (!currentForm.id) return;

        const totalAmount = currentForm.items.reduce((acc, i) => acc + i.totalValue, 0);

        setContracts(prev => prev.map(p => {
            if (p.id === currentForm.id) {
                return {
                    ...p,
                    subcontractorId: currentForm.subcontractorId,
                    startDate: currentForm.startDate,
                    endDate: currentForm.endDate,
                    retention: currentForm.retention,
                    items: currentForm.items,
                    totalAmount: totalAmount,
                    status: 'Activo'
                };
            }
            return p;
        }));
        setAddStatementModalOpen(false);
    };

    // 7. Handle View EP Detail from List
    // We are viewing the detail of an existing EP here (Read Only view usually)
    const handleOpenEPDetailFromList = (ep: PaymentStatement) => {
        setSelectedEPForView(ep);
        setEpDetailModalOpen(true);
        // Initialize retention from contract if available
        const contract = contracts.find(c => c.id === ep.contractId);
        if (contract) {
            setRetentionPercentageInView(contract.retention || 0);
        }
    };

    // Helpers
    const getSubcontractorById = (id: string) => subcontractors.find(s => s.id === id);
    const selectedContract = contracts.find(ep => ep.id === selectedContractId);

    // Check if the currently editing form belongs to an Authorized contract
    const isEditingAuthorized = currentForm.id
        ? contracts.find(p => p.id === currentForm.id)?.status === 'Activo'
        : false;

    // Check if a unit (or EDT anexo) is already assigned to ANY contract
    const isUnitAlreadyContracted = (unitId: string, taskId: string, excludeContractId?: string) => {
        return contracts.some(c => {
            if (excludeContractId && c.id === excludeContractId) return false;
            // Check items in base contract
            return c.items.some(item => {
                if (item.id !== taskId) return false;
                return item.details.some(d => d.unitId === unitId);
            }) || (c.anexos || []).some(anexo => {
                // Check items in active anexos
                if (anexo.status === 'Anulado') return false;
                return anexo.items.some(item => {
                    if (item.id !== taskId) return false;
                    return item.details.some(d => d.unitId === unitId);
                });
            });
        });
    };

    // Get valid units for the currently selected task (those that have a value in matrix)
    const getValidUnitsForTask = (taskId: string) => {
        const validResults: any[] = [];
        units.forEach(u => {
            // 1. Check Base Value
            const rawVal = matrixData[`payment_${taskId}_${u.id}`];
            const val = rawVal ? parseInt(rawVal.replace(/\D/g, ''), 10) : 0;

            if (val > 0) {
                const qty = matrixData[`payment_qty_${taskId}_${u.id}`] || '';
                const unitPrice = matrixData[`payment_unit_${taskId}_${u.id}`] || '';

                validResults.push({
                    ...u,
                    value: val,
                    quantity: qty,
                    unitPrice: unitPrice
                });
            }

            // 2. Check for Anexos in EDT
            const anexosKey = `payment_anexos_${taskId}_${u.id}`;
            const anexosJson = matrixData[anexosKey];
            if (anexosJson) {
                try {
                    const anexos = JSON.parse(anexosJson);
                    anexos.forEach((a: any) => {
                        const anexoTotal = (parseFloat(a.quantity) || 0) * (parseFloat(a.unitPrice) || 0);
                        if (anexoTotal > 0) {
                            validResults.push({
                                ...u,
                                id: `${u.id}_${a.id}`, // Unique composite ID for selection
                                parentUnitId: u.id,
                                name: `${u.name} (${a.name})`,
                                value: anexoTotal,
                                quantity: a.quantity,
                                unitPrice: a.unitPrice,
                                isEdtAnexo: true
                            });
                        }
                    });
                } catch (e) {}
            }
        });

        // Filter by Exclusivity (unless we are EDITING the current contract/anexo and it's already there)
        // For simplicity in this mock, we filter out everything already contracted.
        return validResults.filter(r => !isUnitAlreadyContracted(r.id, taskId));
    };

    // Check if a protocol allows payment for a task/unit based on reviewer flags
    const isProtocolPayable = (taskId: string, unitId: string) => {
        return Object.values(savedProtocols).some((p: ProtocolState) => {
            if (p.taskId === taskId && p.unitId === unitId) {
                const config = protocolConfigs[taskId];
                const group = p.groupId
                    ? config?.reviewerGroups?.find(g => g.id === p.groupId)
                    : config?.reviewerGroups?.find(g => g.unitIds.includes(p.unitId));

                if (group) {
                    return group.reviewers.some((rev, idx) =>
                        rev.generatesPayment && p.signatures[idx]
                    );
                }
            }
            return false;
        });
    };

    const handleCancelPaymentStatement = (epId: string) => {
        const ep = paymentStatements.find(p => p.id === epId);
        if (!ep) return;

        if (!confirm(`¿Está seguro que desea anular el Estado de Pago #${paymentStatements.filter(p => p.contractId === ep.contractId).findIndex(p => p.id === epId) + 1}? Esta acción no se puede deshacer.`)) {
            return;
        }

        setPaymentStatements(prev => prev.map(p => p.id === epId ? { ...p, status: 'Anulado' } : p));

        const subName = getSubcontractorById(ep.subcontractorId)?.razonSocial || 'Desconocido';
        addActivity({
            type: 'subcontracts',
            title: 'Estado de Pago Anulado',
            description: `Se ha anulado el EP por ${ep.amount} para el subcontratista ${subName}`,
            user: 'Usuario'
        });
    };

    // Recursive task renderer for selector
    const renderTaskSelectorItems = (list: Task[], level = 0, indexPrefix = "") => {
        let items: React.ReactNode[] = [];
        list.forEach((t, idx) => {
            const currentIndex = indexPrefix ? `${indexPrefix}.${idx + 1}` : `${idx + 1}`;
            const padding = level * 20 + 16;

            const isFolder = t.type === 'folder' || (t.children && t.children.length > 0);

            if (isFolder) {
                items.push(
                    <div key={t.id} className="py-2 px-4 bg-slate-50 font-bold text-slate-700 text-sm border-b border-slate-100 whitespace-nowrap min-w-max" style={{ paddingLeft: padding }}>
                        {currentIndex} {t.name}
                    </div>
                );
                if (t.children) items = [...items, ...renderTaskSelectorItems(t.children, level + 1, currentIndex)];
            } else {
                const hasAssignedUnits = units.some(u => {
                    const rawVal = matrixData[`payment_${t.id}_${u.id}`];
                    return rawVal ? parseInt(rawVal.replace(/\D/g, ''), 10) > 0 : false;
                });

                items.push(
                    <div
                        key={t.id}
                        onClick={() => handleTaskSelect(t, currentIndex)}
                        className="py-2 px-4 hover:bg-blue-50 cursor-pointer text-sm text-slate-600 border-b border-slate-100 flex justify-between items-center group whitespace-nowrap min-w-max gap-4"
                        style={{ paddingLeft: padding }}
                    >
                        <div className="flex items-center gap-2">
                            {hasAssignedUnits ? (
                                <span className="material-symbols-outlined text-[16px] text-green-600 font-bold shrink-0" title="Tiene presupuesto asignado">attach_money</span>
                            ) : (
                                <span className="w-[16px] shrink-0" />
                            )}
                            <span><span className="font-mono text-slate-400 mr-2">{currentIndex}</span>{t.name}</span>
                        </div>
                        <span className="material-symbols-outlined text-[18px] text-slate-300 group-hover:text-primary shrink-0">arrow_forward</span>
                    </div>
                );
            }
        });
        return items;
    };

    // Helper to find task by ID in deep structure
    const findTaskById = (id: string, list: Task[]): Task | null => {
        for (const t of list) {
            if (t.id === id) return t;
            if (t.children) {
                const found = findTaskById(id, t.children);
                if (found) return found;
            }
        }
        return null;
    };

    const renderSubcontractTypeModal = () => {
        if (!subcontractTypeModalOpen) return null;

        const filteredTypes = subcontractTypes.filter(t =>
            t.name.toLowerCase().includes(typeSearchQuery.toLowerCase())
        );

        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-lg border border-slate-200 dark:border-slate-800 flex flex-col max-h-[80vh]">
                    <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50 rounded-t-xl">
                        <div className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-blue-600">category</span>
                            <h3 className="text-xl font-bold text-slate-800 dark:text-white">Tipos de Subcontrato</h3>
                        </div>
                        <button onClick={() => setSubcontractTypeModalOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>

                    <div className="p-6 flex flex-col gap-6 overflow-hidden">
                        {/* New Type Creation */}
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-sm">add_circle</span>
                                <input
                                    type="text"
                                    placeholder="Nombre del nuevo tipo..."
                                    value={newTypeName}
                                    onChange={(e) => setNewTypeName(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border-0 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 transition-all font-medium"
                                    onKeyDown={(e) => e.key === 'Enter' && handleCreateSubcontractType()}
                                />
                            </div>
                            <button
                                onClick={handleCreateSubcontractType}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-md hover:bg-blue-700 transition-colors"
                            >
                                + Crear
                            </button>
                        </div>

                        {/* Search Bar */}
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-sm">search</span>
                            <input
                                type="text"
                                placeholder="Buscar tipos..."
                                value={typeSearchQuery}
                                onChange={(e) => setTypeSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border-0 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 transition-all"
                            />
                        </div>

                        {/* List */}
                        <div className="flex-1 overflow-y-auto min-h-[300px] border border-slate-100 dark:border-slate-800 rounded-lg">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-slate-50 dark:bg-slate-800/50 sticky top-0">
                                    <tr>
                                        <th className="px-4 py-2 text-xs font-bold text-slate-500 uppercase">Nombre del Tipo</th>
                                        <th className="px-4 py-2 text-xs font-bold text-slate-500 uppercase text-right w-20">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTypes.length === 0 ? (
                                        <tr>
                                            <td colSpan={2} className="px-4 py-8 text-center text-slate-500 italic text-sm">
                                                {typeSearchQuery ? 'No se encontraron resultados' : 'No hay tipos creados'}
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredTypes.map(type => (
                                            <tr key={type.id} className="border-t border-slate-50 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">
                                                <td className="px-4 py-3 text-sm font-medium text-slate-700 dark:text-slate-300">{type.name}</td>
                                                <td className="px-4 py-3 text-right">
                                                    <button
                                                        onClick={() => setSubcontractTypes(subcontractTypes.filter(t => t.id !== type.id))}
                                                        className="text-slate-300 hover:text-red-500 transition-colors p-1"
                                                    >
                                                        <span className="material-symbols-outlined text-lg">delete</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="p-6 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                        <button
                            onClick={() => setSubcontractTypeModalOpen(false)}
                            className="px-6 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderEPDetailModal = () => {
        if (!selectedEPForView) return null;
        const selectedContractForDetail = contracts.find(c => c.id === selectedEPForView.contractId);
        if (!selectedContractForDetail) return null;

        const sub = getSubcontractorById(selectedEPForView.subcontractorId);

        const allContractEPs = paymentStatements
            .filter(ep => ep.contractId === selectedContractForDetail.id);

        const currentEpIndex = allContractEPs.findIndex(ep => ep.id === selectedEPForView.id);
        const previousEPs = allContractEPs.slice(0, currentEpIndex);

        // Calculate Totals for Summary Table
        let totalCurrentEP = 0;

        // Group contract items and details
        const groupedRows = selectedContractForDetail.items.map(contractItem => {
            const taskId = contractItem.id;
            const task = findTaskById(taskId, tasks);

            let itemTotalContractualQty = 0;
            let itemTotalUnitPrice = 0;
            let itemTotalValue = 0;
            let itemAccQty = 0;
            let itemAccTotal = 0;
            let itemPrevQty = 0;
            let itemPrevTotal = 0;
            let itemCurrentQty = 0;
            let itemCurrentTotal = 0;

            // Arrays to calculate averages
            const accPercents: number[] = [];
            const prevPercents: number[] = [];
            const currentPercents: number[] = [];

            const currentItem = selectedEPForView.items?.find(i => i.id === taskId);
            const selectedUnitIds = currentItem?.selectedUnitIds || [];

            const details = contractItem.details.map(detail => {
                const unitId = detail.unitId;
                const totalQty = parseFloat(detail.quantity || '0') || 0;
                const unitPrice = parseFloat(detail.unitPrice || '0') || 0;
                const totalValue = totalQty * unitPrice;

                // Previous
                let prevQty = 0;
                previousEPs.forEach(pEp => {
                    const pItem = pEp.items?.find(i => i.id === taskId);
                    // Check if unit was actually selected in this previous EP
                    if (pItem?.selectedUnitIds?.includes(unitId)) {
                        const pDetail = pItem?.details.find(d => d.unitId === unitId);
                        prevQty += parseFloat(pDetail?.quantity || '0') || 0;
                    }
                });

                // Current - filter by selectedUnitIds
                let currentQty = 0;
                if (selectedUnitIds.includes(unitId)) {
                    const currentDetail = currentItem?.details.find(d => d.unitId === unitId);
                    currentQty = parseFloat(currentDetail?.quantity || '0') || 0;
                }

                const accQty = prevQty + currentQty;

                itemTotalContractualQty += totalQty;
                itemTotalUnitPrice += unitPrice;
                itemTotalValue += totalValue;
                itemAccQty += accQty;
                itemAccTotal += accQty * unitPrice;
                itemPrevQty += prevQty;
                itemPrevTotal += prevQty * unitPrice;
                itemCurrentQty += currentQty;
                itemCurrentTotal += currentQty * unitPrice;

                // For averages
                if (totalQty > 0) {
                    accPercents.push((accQty / totalQty) * 100);
                    prevPercents.push((prevQty / totalQty) * 100);
                    currentPercents.push((currentQty / totalQty) * 100);
                }

                return {
                    unitId,
                    unitName: detail.unitName,
                    totalQty,
                    unitPrice,
                    totalValue,
                    acc: { qty: accQty, total: accQty * unitPrice },
                    prev: { qty: prevQty, total: prevQty * unitPrice },
                    current: { qty: currentQty, total: currentQty * unitPrice }
                };
            });

            const avgAcc = accPercents.length > 0 ? Math.round(accPercents.reduce((a, b) => a + b, 0) / accPercents.length) : 0;
            const avgPrev = prevPercents.length > 0 ? Math.round(prevPercents.reduce((a, b) => a + b, 0) / prevPercents.length) : 0;
            const avgCurrent = currentPercents.length > 0 ? Math.round(currentPercents.reduce((a, b) => a + b, 0) / currentPercents.length) : 0;

            totalCurrentEP += itemCurrentTotal;

            return {
                id: taskId,
                code: contractItem.code,
                name: contractItem.description,
                unit: task?.unit || '-',
                totalContractualQty: itemTotalContractualQty,
                totalUnitPrice: itemTotalUnitPrice,
                totalValue: itemTotalValue,
                acc: { qty: itemAccQty, total: itemAccTotal, percent: avgAcc },
                prev: { qty: itemPrevQty, total: itemPrevTotal, percent: avgPrev },
                current: { qty: itemCurrentQty, total: itemCurrentTotal, percent: avgCurrent },
                details
            };
        });

        // Financial Calculations
        const retentionAmount = totalCurrentEP * (retentionPercentageInView / 100);
        const netAmount = totalCurrentEP - retentionAmount;
        const ivaAmount = netAmount * 0.19;
        const totalToPay = netAmount + ivaAmount;

        const toggleItem = (id: string) => {
            const next = new Set(expandedItemsInView);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            setExpandedItemsInView(next);
        };

        const handleKillModal = () => {
            setEpDetailModalOpen(false);
            setExpandedItemsInView(new Set());
        };

        const handlePrint = () => {
            // Get the HTML content of the caratula, minus the header
            const contentElement = document.getElementById('ep-caratula-content');
            if (!contentElement) return;

            // Create a hidden iframe
            const iframe = document.createElement('iframe');
            iframe.style.position = 'fixed';
            iframe.style.right = '0';
            iframe.style.bottom = '0';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = '0';
            document.body.appendChild(iframe);

            const iframeDoc = iframe.contentWindow?.document;
            if (!iframeDoc) return;

            // Build the print document
            // We include Tailwind via CDN to ensure styles match exactly without needing the main app's CSS block constraints
            iframeDoc.open();
            iframeDoc.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Caratula EP</title>
                    <script src="https://cdn.tailwindcss.com"></script>
                    <style>
                        @page { margin: 1cm; }
                        body { 
                            background: white; 
                            -webkit-print-color-adjust: exact; 
                            print-color-adjust: exact; 
                            font-family: system-ui, -apple-system, sans-serif;
                        }
                        /* Hide things that shouldn't print (like collapse icons or inputs) */
                        .print-hidden { display: none !important; }
                        /* Ensure inputs are printed as text if needed */
                        input { border: none !important; padding: 0 !important; margin: 0 !important; width: auto !important; }
                    </style>
                </head>
                <body>
                    <div style="padding: 20px;">
                        ${contentElement.innerHTML}
                    </div>
                </body>
                </html>
            `);
            iframeDoc.close();

            // Wait for Tailwind to process and images to load
            setTimeout(() => {
                iframe.contentWindow?.focus();
                iframe.contentWindow?.print();
                // Clean up iframe after printing
                setTimeout(() => {
                    document.body.removeChild(iframe);
                }, 1000);
            }, 1000); // 1 second delay to ensure styles apply
        };

        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                <div id="ep-caratula-container" className="bg-white dark:bg-slate-900 shadow-2xl w-full max-w-7xl border border-slate-300 flex flex-col h-[95vh] overflow-hidden">
                    {/* Header bar */}
                    <div className="bg-slate-100 dark:bg-slate-800 p-2 md:p-3 border-b border-slate-300 flex flex-wrap md:flex-nowrap justify-between items-center shrink-0 gap-2">
                        <div className="flex gap-2 md:gap-4 items-center w-full md:w-auto justify-between md:justify-start">
                            <button onClick={handlePrint} className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 bg-blue-600 text-white rounded font-bold hover:bg-blue-700 text-xs md:text-sm">
                                <span className="material-symbols-outlined text-[16px] md:text-[20px]">print</span>
                                Exportar PDF
                            </button>
                            <div className="text-[10px] md:text-xs text-slate-500 italic hidden sm:block">Los items desplegados aparecerán así en el PDF.</div>
                            <button onClick={handleKillModal} className="text-slate-500 hover:text-slate-800 md:hidden ml-auto">
                                <span className="material-symbols-outlined text-[20px]">close</span>
                            </button>
                        </div>
                        <button onClick={handleKillModal} className="text-slate-500 hover:text-slate-800 hidden md:block">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>

                    <div id="ep-caratula-content" className="flex-1 overflow-auto p-4 md:p-8 bg-white text-slate-900">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                            <div className="flex items-center gap-3 md:gap-4">
                                {companyConfig?.logo || '/assets/logo_ld.png' ? (
                                    <>
                                        <img src={companyConfig?.logo || '/assets/logo_ld.png'} alt="Logo" className="hidden md:block h-10 md:h-12 lg:h-16 max-w-[80px] md:max-w-xs object-contain" />
                                        <img src="/assets/play_store_icon.png" alt="K-LyDapp" className="block md:hidden h-10 object-contain rounded-md" />
                                    </>
                                ) : (
                                    <div className="w-10 h-10 md:w-16 md:h-16 bg-blue-600 flex items-center justify-center text-white font-bold rounded text-xs md:text-base">LOGO</div>
                                )}
                                <div>
                                    <div className="text-sm md:text-xl font-black text-slate-900 uppercase leading-tight">{companyConfig?.name || 'K-LYD SYSTEM'}</div>
                                    <div className="text-[10px] md:text-xs text-slate-600 font-bold">RUT: {companyConfig?.rut || '-'}</div>
                                    <div className="text-[10px] md:text-xs text-slate-600 uppercase hidden sm:block">{companyConfig?.direccion || '-'}</div>
                                </div>
                            </div>
                            <div className="bg-blue-700 text-white p-1.5 px-3 md:p-2 md:px-6 font-bold text-[10px] md:text-sm uppercase tracking-wider rounded-sm shrink-0 w-full md:w-auto text-center">
                                CARATULA ESTADO DE PAGO
                            </div>
                        </div>

                        {/* Info Section */}
                        <div className="grid grid-cols-2 gap-x-12 gap-y-1 mb-8 text-xs text-slate-800">
                            <div className="flex gap-2"><span className="font-bold w-32">N° ESTADO DE PAGO:</span> <span>{currentEpIndex + 1}</span></div>
                            <div className="flex gap-2"><span className="font-bold w-32">R.U.T:</span> <span>{sub?.rut}</span></div>
                            <div className="flex gap-2"><span className="font-bold w-32">RAZÓN SOCIAL:</span> <span>{sub?.razonSocial}</span></div>
                            <div className="flex gap-2"><span className="font-bold w-32">FECHA DE EMISIÓN:</span> <span>{selectedEPForView.endDate || 'N/A'}</span></div>
                        </div>

                        {/* Main Table */}
                        <table className="w-full border-collapse border border-slate-400 text-[10px] mb-8">
                            <thead>
                                <tr className="bg-slate-100">
                                    <th className="border border-slate-400 px-1 py-2 font-bold w-10 text-center" rowSpan={2}>Cod</th>
                                    <th className="border border-slate-400 px-1 py-2 font-bold text-left" rowSpan={2}>Partidas / Detalle Unidades</th>
                                    <th className="border border-slate-400 px-1 py-2 font-bold w-16 text-center" rowSpan={2}>Nivel</th>
                                    <th className="border border-slate-400 px-1 py-2 font-bold w-12 text-center" rowSpan={2}>Unidad</th>
                                    <th className="border border-slate-400 px-1 py-2 font-bold w-16 text-center" rowSpan={2}>Cant. Contractual</th>
                                    <th className="border border-slate-400 px-1 py-2 font-bold w-20 text-right" rowSpan={2}>Val. Unitario</th>
                                    <th className="border border-slate-400 px-1 py-2 font-bold w-20 text-right" rowSpan={2}>Valor Total</th>
                                    <th className="border border-slate-400 px-1 py-2 font-bold text-center" colSpan={3}>Avance Acumulado</th>
                                    <th className="border border-slate-400 px-1 py-2 font-bold text-center" colSpan={3}>Avance Anterior</th>
                                    <th className="border border-slate-400 px-1 py-2 font-bold text-center" colSpan={3}>Avance Actual EP</th>
                                </tr>
                                <tr className="bg-slate-100">
                                    <th className="border border-slate-400 px-1 py-1 text-center w-14">Cant.</th>
                                    <th className="border border-slate-400 px-1 py-1 text-center w-8">%</th>
                                    <th className="border border-slate-400 px-1 py-1 text-right w-20">Total $</th>
                                    <th className="border border-slate-400 px-1 py-1 text-center w-14">Cant.</th>
                                    <th className="border border-slate-400 px-1 py-1 text-center w-8">%</th>
                                    <th className="border border-slate-400 px-1 py-1 text-right w-20">Total $</th>
                                    <th className="border border-slate-400 px-1 py-1 text-center w-14">Cant.</th>
                                    <th className="border border-slate-400 px-1 py-1 text-center w-8">%</th>
                                    <th className="border border-slate-400 px-1 py-1 text-right w-20">Total $</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupedRows.map((row) => (
                                    <React.Fragment key={row.id}>
                                        <tr className="bg-slate-50 cursor-pointer hover:bg-slate-100" onClick={() => toggleItem(row.id)}>
                                            <td className="border border-slate-400 px-1 py-1.5 text-center font-bold">{row.code}</td>
                                            <td className="border border-slate-400 px-1 py-1.5 font-bold">
                                                <div className="flex items-center gap-1">
                                                    <span className="material-symbols-outlined text-[14px] print-hidden">{expandedItemsInView.has(row.id) ? 'expand_less' : 'expand_more'}</span>
                                                    {row.name}
                                                </div>
                                            </td>
                                            <td className="border border-slate-400 px-1 py-1.5 text-center">-</td>
                                            <td className="border border-slate-400 px-1 py-1.5 text-center">{row.unit}</td>
                                            <td className="border border-slate-400 px-1 py-1.5 text-center font-bold">{row.totalContractualQty.toLocaleString()}</td>
                                            <td className="border border-slate-400 px-1 py-1.5 text-right font-bold">{row.totalUnitPrice.toLocaleString()}</td>
                                            <td className="border border-slate-400 px-1 py-1.5 text-right font-bold font-mono">{row.totalValue.toLocaleString()}</td>

                                            <td className="border border-slate-400 px-1 py-1.5 text-center font-bold">{row.acc.qty.toLocaleString()}</td>
                                            <td className="border border-slate-400 px-1 py-1.5 text-center">{truncateProgress(row.acc.percent)}%</td>
                                            <td className="border border-slate-400 px-1 py-1.5 text-right font-bold font-mono">{row.acc.total.toLocaleString()}</td>

                                            <td className="border border-slate-400 px-1 py-1.5 text-center font-bold">{row.prev.qty.toLocaleString()}</td>
                                            <td className="border border-slate-400 px-1 py-1.5 text-center">{truncateProgress(row.prev.percent)}%</td>
                                            <td className="border border-slate-400 px-1 py-1.5 text-right font-bold font-mono">{row.prev.total.toLocaleString()}</td>

                                            <td className="border border-slate-400 px-1 py-1.5 text-center font-bold bg-yellow-50">{row.current.qty.toLocaleString()}</td>
                                            <td className="border border-slate-400 px-1 py-1.5 text-center bg-yellow-50">{truncateProgress(row.current.percent)}%</td>
                                            <td className="border border-slate-400 px-1 py-1.5 text-right font-bold font-mono bg-yellow-50">{row.current.total.toLocaleString()}</td>
                                        </tr>
                                        {expandedItemsInView.has(row.id) && row.details.map((d, dIdx) => (
                                            <tr key={`${row.id}-${dIdx}`} className="text-slate-600 bg-white">
                                                <td className="border border-slate-400 px-1 py-1 text-center italic opacity-70"> - </td>
                                                <td className="border border-slate-400 px-1 py-1 pl-4 italic">{d.unitName}</td>
                                                <td className="border border-slate-400 px-1 py-1 text-center">{levels.find(l => l.id === units.find(u => u.id === d.unitId)?.levelId)?.name || "-"}</td>
                                                <td className="border border-slate-400 px-1 py-1 text-center italic opacity-70"> - </td>
                                                <td className="border border-slate-400 px-1 py-1 text-center">{d.totalQty.toLocaleString()}</td>
                                                <td className="border border-slate-400 px-1 py-1 text-right font-mono">{d.unitPrice.toLocaleString()}</td>
                                                <td className="border border-slate-400 px-1 py-1 text-right font-mono italic opacity-70">{d.totalValue.toLocaleString()}</td>

                                                <td className="border border-slate-400 px-1 py-1 text-center">{d.acc.qty.toLocaleString()}</td>
                                                <td className="border border-slate-400 px-1 py-1 text-center">{d.totalQty > 0 ? truncateProgress((d.acc.qty / d.totalQty) * 100) : 0}%</td>
                                                <td className="border border-slate-400 px-1 py-1 text-right font-mono">{d.acc.total.toLocaleString()}</td>

                                                <td className="border border-slate-400 px-1 py-1 text-center">{d.prev.qty.toLocaleString()}</td>
                                                <td className="border border-slate-400 px-1 py-1 text-center">{d.totalQty > 0 ? truncateProgress((d.prev.qty / d.totalQty) * 100) : 0}%</td>
                                                <td className="border border-slate-400 px-1 py-1 text-right font-mono">{d.prev.total.toLocaleString()}</td>

                                                <td className="border border-slate-400 px-1 py-1 text-center font-semibold bg-yellow-50/30">{d.current.qty.toLocaleString()}</td>
                                                <td className="border border-slate-400 px-1 py-1 text-center bg-yellow-50/30 font-semibold">{d.totalQty > 0 ? truncateProgress((d.current.qty / d.totalQty) * 100) : 0}%</td>
                                                <td className="border border-slate-400 px-1 py-1 text-right font-bold font-mono bg-yellow-50/30">{d.current.total.toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>

                        {/* Payment Summary Table */}
                        <div className="flex justify-end pr-2">
                            <table className="w-80 border-collapse border border-black text-xs text-slate-800">
                                <thead>
                                    <tr>
                                        <th colSpan={2} className="border border-black bg-slate-100 py-1 font-bold text-center uppercase">RESUMEN DE PAGO</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="border border-black px-2 py-1.5 font-bold bg-slate-50 uppercase">ESTADO DE PAGO</td>
                                        <td className="border border-black px-2 py-1.5 text-right font-mono font-bold">${totalCurrentEP.toLocaleString()}</td>
                                    </tr>
                                    <tr>
                                        <td className="border border-black px-2 py-1.5 font-bold flex items-center justify-between uppercase">
                                            <span>% RETENCIÓN</span>
                                            <div className="flex items-center gap-1">
                                                <input
                                                    type="number"
                                                    value={retentionPercentageInView}
                                                    onChange={(e) => setRetentionPercentageInView(parseFloat(e.target.value) || 0)}
                                                    className="w-12 border border-slate-300 px-1 text-right print-hidden"
                                                />
                                                <span className="hidden print:inline">{truncateProgress(retentionPercentageInView)}%</span>
                                            </div>
                                        </td>
                                        <td className="border border-black px-2 py-1.5 text-right font-mono">{truncateProgress(retentionPercentageInView)}%</td>
                                    </tr>
                                    <tr>
                                        <td className="border border-black px-2 py-1.5 font-bold bg-slate-50 uppercase">RETENCIÓN</td>
                                        <td className="border border-black px-2 py-1.5 text-right font-mono text-red-600">-${retentionAmount.toLocaleString()}</td>
                                    </tr>
                                    <tr>
                                        <td className="border border-black px-2 py-1.5 font-bold bg-slate-50 uppercase">ESTADO DE PAGO NETO</td>
                                        <td className="border border-black px-2 py-1.5 text-right font-mono font-bold">${netAmount.toLocaleString()}</td>
                                    </tr>
                                    <tr>
                                        <td className="border border-black px-2 py-1.5 font-bold bg-slate-50 uppercase">IVA (19%)</td>
                                        <td className="border border-black px-2 py-1.5 text-right font-mono">${ivaAmount.toLocaleString()}</td>
                                    </tr>
                                    <tr className="bg-slate-200">
                                        <td className="border border-black px-2 py-2 font-black uppercase text-sm">VALOR A PAGAR</td>
                                        <td className="border border-black px-2 py-2 text-right font-black font-mono text-sm">${totalToPay.toLocaleString()}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div 
            className="flex flex-col h-full min-h-0 bg-background-light dark:bg-background-dark overflow-hidden"
        >
            {/* Header / Toolbar */}
            <div className="bg-[#1a73e8] dark:bg-blue-900 text-white h-14 px-4 md:px-6 flex items-center justify-between shrink-0 shadow-md z-30">
                <div className="flex items-center gap-6">

                    {/* Menu Item */}
                    <div className="relative">
                        <button
                            onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
                            className={`flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded transition-colors ${menuOpen ? 'bg-white/20' : 'hover:bg-white/10'}`}
                        >
                            <span className="material-symbols-outlined text-[18px]">handshake</span>
                            Empresas Contratistas
                            <span className="material-symbols-outlined text-[16px]">expand_more</span>
                        </button>

                        {menuOpen && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)}></div>
                                <div className="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 py-1 z-50 animate-in fade-in zoom-in duration-100">
                                    {hasPermission('sc_config', 'edit') && (
                                        <button
                                            onClick={() => { setMenuOpen(false); setCreateMasterSubModalOpen(true); }}
                                            className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                        >
                                            <span className="material-symbols-outlined text-[18px] text-slate-400">add_business</span>
                                            Crear Empresa Contratista
                                        </button>
                                    )}
                                    <button
                                        onClick={() => { setMenuOpen(false); setListMasterSubModalOpen(true); }}
                                        className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                    >
                                        <span className="material-symbols-outlined text-[18px] text-slate-400">list_alt</span>
                                        Lista de Empresas Subcontratistas
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                
                {/* Right side: Project Name (Desktop) OR Widgets (Mobile) */}
                <div className="flex items-center gap-2">
                    <div className="text-xs text-blue-100 hidden md:block truncate mr-2">Proyecto Activo: {projectInfo.name}</div>
                    
                    {/* Mobile triggers in the blue header bar */}
                    <div className="flex md:hidden items-center gap-1.5 shrink-0">
                        <button 
                            onClick={() => window.dispatchEvent(new CustomEvent('open-bug-report'))}
                            className="w-10 h-10 bg-rose-600 border border-white/20 text-white rounded-xl shadow-md flex items-center justify-center transition-all hover:scale-105 active:scale-95"
                            title="Reportar Falla"
                        >
                            <span className="material-symbols-outlined text-[20px]">bug_report</span>
                        </button>
                        <button 
                            onClick={() => window.dispatchEvent(new CustomEvent('open-ai-assistant'))}
                            className="w-10 h-10 bg-white/20 border border-white/20 text-white rounded-xl shadow-md flex items-center justify-center transition-all hover:scale-105 active:scale-95 backdrop-blur-sm"
                            title="Asistente IA"
                        >
                            <svg viewBox="0 0 100 100" className="w-[20px] h-[20px]" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M25 50 C25 25, 75 25, 75 50" stroke="currentColor" strokeWidth="6" strokeLinecap="round" fill="none" />
                                <rect x="18" y="40" width="8" height="20" rx="4" fill="currentColor" />
                                <rect x="74" y="40" width="8" height="20" rx="4" fill="currentColor" />
                                <circle cx="40" cy="50" r="4.5" fill="currentColor" />
                                <circle cx="60" cy="50" r="4.5" fill="currentColor" />
                                <path d="M42 63 C45 67, 55 67, 58 63" stroke="currentColor" strokeWidth="4" strokeLinecap="round" fill="none" />
                                <path d="M78 56 C78 72, 68 82, 52 82" stroke="currentColor" strokeWidth="4.5" strokeLinecap="round" fill="none" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div 
                className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col gap-6 p-4 md:p-6 lg:p-8 w-full max-w-[1600px] mx-auto pb-[calc(5rem+env(safe-area-inset-bottom))]"
                style={{ 
                    WebkitOverflowScrolling: 'touch'
                }}
            >

                {/* Header Section */}
                <div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white tracking-tight">Empresas Contratistas</h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Gestión de contratos y estados de pago de subcontratos.</p>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap items-center gap-2 mt-4">
                        <button
                            onClick={handleOpenAddStatement}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-white font-bold text-sm hover:bg-blue-600 shadow-sm transition-colors"
                        >
                            <span className="material-symbols-outlined text-[20px]">add</span>
                            Añadir Contratista
                        </button>
                        <button
                            onClick={() => handleEditStatement()}
                            disabled={!selectedContractId || selectedContract?.status === 'Anulado'}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 font-medium text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="material-symbols-outlined text-[20px]">edit</span>
                            Editar
                        </button>

                        <div className="h-6 w-px bg-slate-300 dark:bg-slate-600 mx-1"></div>

                        <button
                            onClick={() => {
                                if (confirm("¿Inactivar este Subcontrato?")) {
                                    setContracts(prev => prev.map(p => p.id === selectedContractId ? { ...p, status: 'Inactivo' } : p));
                                }
                            }}
                            disabled={!selectedContractId || selectedContract?.status === 'Inactivo' || selectedContract?.status === 'Anulado'}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-amber-600 font-medium text-sm hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="material-symbols-outlined text-[20px]">block</span>
                            Inactivar
                        </button>
                        
                        <button
                            onClick={() => {
                                const reason = prompt("Ingrese el motivo de la anulación:");
                                if (reason !== null) {
                                    if (reason.trim() === '') {
                                        alert("Debe ingresar un motivo para anular el subcontrato.");
                                        return;
                                    }
                                    setContracts(prev => prev.map(p => p.id === selectedContractId ? { ...p, status: 'Anulado', notes: (p.notes ? p.notes + '\n' : '') + `Anulado: ${reason}` } : p));
                                    setSelectedContractId(null);
                                }
                            }}
                            disabled={!selectedContractId || selectedContract?.status === 'Anulado'}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-red-600 font-medium text-sm hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="material-symbols-outlined text-[20px]">cancel</span>
                            Anular
                        </button>

                        <div className="h-6 w-px bg-slate-300 dark:bg-slate-600 mx-1"></div>
                        
                        <button
                            onClick={() => {
                                const contractToActivate = contracts.find(c => c.id === selectedContractId);
                                const isValid = contractToActivate && contractToActivate.subcontractorId && contractToActivate.items && contractToActivate.items.length > 0;
                                // Nota: Para una validación estricta podríamos buscar el rut en la lista de subcontractors, pero asumimos que si se pudo crear tiene id e items.
                                if (!isValid) {
                                    alert("El contrato no tiene todos los campos completos para ser activado.");
                                    return;
                                }
                                if (confirm("¿Activar este Subcontrato?")) {
                                    setContracts(prev => prev.map(p => p.id === selectedContractId ? { ...p, status: 'Activo' } : p));
                                }
                            }}
                            disabled={!selectedContractId || selectedContract?.status === 'Activo'}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-green-600 font-medium text-sm hover:bg-green-50 dark:hover:bg-green-900/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="material-symbols-outlined text-[20px]">check_circle</span>
                            Activar
                        </button>

                        {isPlatformAdmin && (
                            <button
                                onClick={() => {
                                    if (confirm("¿Eliminar este Subcontrato de forma permanente?")) {
                                        setContracts(prev => prev.filter(p => p.id !== selectedContractId));
                                        setSelectedContractId(null);
                                    }
                                }}
                                disabled={!selectedContractId}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-red-600 font-medium text-sm hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="material-symbols-outlined text-[20px]">delete</span>
                                Eliminar
                            </button>
                        )}

                        {(isPlatformAdmin || hasPermission('sc_generar_ep', 'edit') || hasPermission('movil_sc_generar_ep', 'edit')) && (
                            <button
                                onClick={handleGenerateEPClick}
                                disabled={!selectedContractId || selectedContract?.status === 'Anulado'}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-primary font-medium text-sm hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="material-symbols-outlined text-[20px]">description</span>
                                Generar EP
                            </button>
                        )}

                        <button
                            onClick={() => setAnexosListModalOpen(true)}
                            disabled={!selectedContractId || selectedContract?.status === 'Anulado'}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 font-medium text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="material-symbols-outlined text-[20px]">attachment</span>
                            Anexo
                        </button>

                        <button
                            onClick={() => setViewEPModalOpen(true)}
                            disabled={!selectedContractId || (!isPlatformAdmin && !hasPermission('sc_ver_ep', 'view') && !hasPermission('movil_sc_ver_ep', 'view'))}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-medium text-sm transition-colors ${!selectedContractId || (!isPlatformAdmin && !hasPermission('sc_ver_ep', 'view') && !hasPermission('movil_sc_ver_ep', 'view')) ? 'text-slate-400 opacity-50 cursor-not-allowed' : 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/10'}`}
                        >
                            <span className="material-symbols-outlined text-[20px]">history</span>
                            Ver EP
                        </button>
                    </div>
                </div>

                {/* Table List of Contracts */}
                <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-visible md:overflow-hidden">
                    <table className="w-full text-left border-collapse hidden md:table">
                        <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                            <tr>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase">Empresa Contratista</th>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase">Vigencia</th>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase">EP</th>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase text-right">Monto Contrato</th>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase text-right">Total Pagado</th>
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase text-center">Estado</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {contracts.length === 0 ? (
                                <tr><td colSpan={6} className="p-8 text-center text-slate-400 italic">No hay contratos registrados. Pulse "+ Añadir" para crear uno.</td></tr>
                            ) : (
                                contracts.map(contract => {
                                    const subName = getSubcontractorById(contract.subcontractorId)?.razonSocial || 'Desconocido';
                                    const isSelected = selectedContractId === contract.id;
                                    const isVoided = contract.status === 'Anulado';

                                    // Calculate Amounts
                                    const activeAnexosTotal = (contract.anexos || [])
                                        .filter(a => a.status === 'Activo')
                                        .reduce((sum, a) => sum + (a.totalAmount || 0), 0);
                                    const contractAmount = (contract.totalAmount || 0) + activeAnexosTotal;
                                    // Calculate paid amount from EPs related to this contract (excluding Drafts and Voided)
                                    const relatedEPs = paymentStatements.filter(ep => ep.contractId === contract.id && ep.status !== 'Borrador' && ep.status !== 'Anulado');
                                    const totalPaid = relatedEPs.reduce((sum, ep) => {
                                        // Simple parsing of "100.000 CLP" -> 100000
                                        const numericAmount = parseInt(ep.amount.replace(/\D/g, ''), 10) || 0;
                                        return sum + numericAmount;
                                    }, 0);

                                    return (
                                        <tr
                                            key={contract.id}
                                            onClick={() => setSelectedContractId(contract.id)}
                                            className={`
                                                cursor-pointer transition-colors
                                                ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}
                                                ${isVoided ? 'opacity-60 grayscale' : ''}
                                            `}
                                        >
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col">
                                                    <span className={`font-bold text-sm ${isSelected ? 'text-primary' : isVoided ? 'text-slate-500 line-through' : 'text-slate-800 dark:text-slate-200'}`}>{subName}</span>
                                                    <span className="text-xs text-slate-400">ID: {contract.id}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
                                                {contract.startDate && contract.endDate ? (
                                                    <span className="font-mono text-xs">{contract.startDate} <span className="text-slate-400 mx-1">➜</span> {contract.endDate}</span>
                                                ) : (
                                                    <span className="text-slate-400 italic">Sin fecha</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-bold text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">{relatedEPs.length}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right font-mono font-medium text-slate-700 dark:text-slate-300 text-sm">
                                                ${contractAmount.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-right font-mono font-bold text-slate-900 dark:text-white text-sm">
                                                ${totalPaid.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`
                                                    px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide
                                                    ${contract.status === 'Borrador' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                                                        contract.status === 'Anulado' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                                        contract.status === 'Activo' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                                                            contract.status === 'Inactivo' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                                                                'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'}
                                                `}>
                                                    {contract.status}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>

                    {/* VISTA MÓVIL: Tarjetas */}
                    <div className="md:hidden flex flex-col p-4 gap-3 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-200 pb-28">
                        {contracts.length === 0 ? (
                            <div className="p-8 text-center text-slate-400 italic">No hay contratos registrados.</div>
                        ) : (
                            contracts.map(contract => {
                                const subName = getSubcontractorById(contract.subcontractorId)?.razonSocial || 'Desconocido';
                                const isSelected = selectedContractId === contract.id;
                                const isVoided = contract.status === 'Anulado';
                                
                                const activeAnexosTotal = (contract.anexos || [])
                                    .filter(a => a.status === 'Activo')
                                    .reduce((sum, a) => sum + (a.totalAmount || 0), 0);
                                const contractAmount = (contract.totalAmount || 0) + activeAnexosTotal;
                                const relatedEPs = paymentStatements.filter(ep => ep.contractId === contract.id);
                                const totalPaid = relatedEPs.reduce((sum, ep) => sum + (parseInt(ep.amount.replace(/\D/g, ''), 10) || 0), 0);

                                return (
                                    <div
                                        key={contract.id}
                                        onClick={() => setSelectedContractId(contract.id)}
                                        className={`rounded-xl border ${isSelected ? 'border-primary ring-2 ring-primary/20 shadow-md' : 'border-slate-200'} p-4 flex flex-col gap-3 transition-all ${isVoided ? 'opacity-60 bg-slate-100' : 'bg-white'}`}
                                    >
                                        <div className="flex justify-between items-start">
                                            <div className="flex flex-col">
                                                <h4 className={`font-bold text-base ${isVoided ? 'line-through text-slate-500' : 'text-slate-800'}`}>{subName}</h4>
                                                <span className="text-xs text-slate-400">ID: {contract.id}</span>
                                            </div>
                                            <span className={`
                                                px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider
                                                ${contract.status === 'Borrador' ? 'bg-blue-100 text-blue-700' :
                                                    contract.status === 'Anulado' ? 'bg-red-100 text-red-700' :
                                                        contract.status === 'Activo' ? 'bg-green-100 text-green-700' :
                                                            contract.status === 'Inactivo' ? 'bg-orange-100 text-orange-700' :
                                                                'bg-slate-100 text-slate-700'}
                                            `}>
                                                {contract.status}
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-2 gap-2 text-sm mt-1">
                                            <div className="flex flex-col bg-slate-50 p-2 rounded">
                                                <span className="text-[10px] text-slate-500 font-bold uppercase">Monto Contrato</span>
                                                <span className="font-mono font-medium">${contractAmount.toLocaleString()}</span>
                                            </div>
                                            <div className="flex flex-col bg-slate-50 p-2 rounded">
                                                <span className="text-[10px] text-slate-500 font-bold uppercase">Total Pagado</span>
                                                <span className="font-mono font-bold text-primary">${totalPaid.toLocaleString()}</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                                            <div className="text-xs text-slate-500">
                                                {contract.startDate && contract.endDate ? (
                                                    <span className="font-mono">{contract.startDate} ➜ {contract.endDate}</span>
                                                ) : 'Sin fecha'}
                                            </div>
                                            <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-100 rounded text-xs font-bold text-slate-600">
                                                <span className="material-symbols-outlined text-[14px]">receipt_long</span>
                                                {relatedEPs.length} EPs
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>

            {/* --- MODALS --- */}

            {/* View EP History Modal */}
            {viewEPModalOpen && selectedContract && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-full max-w-4xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[80vh]">
                        {/* Blue Header */}
                        <div className="bg-blue-600 p-4 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-white uppercase tracking-wider">ESTADOS DE PAGO - {getSubcontractorById(selectedContract.subcontractorId)?.razonSocial}</h3>
                            <button onClick={() => setViewEPModalOpen(false)} className="text-white/80 hover:text-white">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="p-8 overflow-y-auto bg-white dark:bg-slate-900 flex flex-col gap-4">
                            {(() => {
                                // Filter EPs for the currently selected CONTRACT
                                const relatedEPs = paymentStatements.filter(ep => ep.contractId === selectedContract.id);

                                if (relatedEPs.length === 0) {
                                    return (
                                        <div className="text-center py-12 text-slate-500">
                                            <span className="material-symbols-outlined text-5xl opacity-30 mb-2">folder_off</span>
                                            <p className="text-lg">No se han generado Estados de Pago para este Contrato.</p>
                                        </div>
                                    );
                                }

                                return relatedEPs.map((ep, index) => {
                                    const isVoided = ep.status === 'Anulado';
                                    return (
                                        <div
                                            key={ep.id}
                                            onClick={() => !isVoided && handleOpenEPDetailFromList(ep)}
                                            className={`
                                                border-2 rounded-xl p-4 transition-colors shadow-sm
                                                ${isVoided 
                                                    ? 'bg-slate-100/50 dark:bg-slate-800/30 border-slate-300 dark:border-slate-700 opacity-50 grayscale cursor-default' 
                                                    : 'bg-blue-200/50 dark:bg-blue-900/30 border-slate-700 dark:border-slate-500 cursor-pointer hover:bg-blue-200 hover:border-blue-800 dark:hover:bg-blue-900/50'}
                                            `}
                                        >
                                            <div className="flex flex-col gap-1">
                                                <div className="flex justify-between items-start">
                                                    <div className="flex flex-col gap-1">
                                                        <h4 className="text-white text-lg font-bold drop-shadow-sm uppercase" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                                                            {isVoided ? (
                                                                <span className="text-slate-500 dark:text-slate-400 italic">ESTADO DE PAGO (ANULADO)</span>
                                                            ) : (
                                                                <span className="text-slate-700 dark:text-white">ESTADO DE PAGO #{index + 1}</span>
                                                            )}
                                                        </h4>
                                                        <div className="text-white text-sm font-medium drop-shadow-sm" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                                                            <span className="text-slate-600 dark:text-slate-300">Fecha de EP: </span>
                                                            <span className="text-slate-800 dark:text-white font-bold">{ep.endDate || 'Pendiente'}</span>
                                                        </div>
                                                        <div className="text-white text-sm font-medium drop-shadow-sm" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                                                            <span className="text-slate-600 dark:text-slate-300">Monto Pagado: </span>
                                                            <span className="text-slate-800 dark:text-white font-bold">{ep.amount}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col items-end gap-2">
                                                        <span className={`text-xs px-2 py-1 rounded text-white ${isVoided ? 'bg-slate-500' : 'bg-slate-700'}`}>{ep.status}</span>
                                                        {!isVoided && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleCancelPaymentStatement(ep.id);
                                                                }}
                                                                className={`flex items-center gap-1 text-[10px] font-bold text-red-600 hover:text-red-700 bg-white/50 dark:bg-white/10 px-2 py-1 rounded transition-colors border border-red-200 dark:border-red-900/30 ${!hasPermission("sc_ep", "edit") ? "opacity-50 pointer-events-none" : ""}`}
                                                            >
                                                                <span className="material-symbols-outlined text-[14px]">block</span>
                                                                Anular EP
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* --- GENERATE EP WORKFLOW MODALS --- */}

            {/* 1. Generate EP Main Modal (Summary) */}
            {generateEpOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-none shadow-2xl w-full max-w-6xl border border-slate-300 dark:border-slate-700 flex flex-col h-[90vh] overflow-hidden">

                        {/* Blue Header */}
                        <div className="bg-blue-600 px-6 py-4">
                            <h3 className="text-lg font-bold text-white uppercase tracking-wider">ESTADO DE PAGO</h3>
                        </div>

                        {/* Info Section */}
                        <div className="p-6 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                            <div className="flex flex-col gap-1 mb-4">
                                <h4 className="text-sm font-bold text-slate-900 dark:text-white">Estado de Pago #{paymentStatements.filter(p => p.contractId === currentDraftEp.contractId).length + 1}</h4>
                                <div className="text-sm text-slate-700 dark:text-slate-300 flex flex-col gap-0.5">
                                    <span><span className="font-semibold">R.U.T:</span> {getSubcontractorById(currentDraftEp.subcontractorId || '')?.rut}</span>
                                    <span><span className="font-semibold uppercase">RAZÓN SOCIAL:</span> {getSubcontractorById(currentDraftEp.subcontractorId || '')?.razonSocial}</span>
                                </div>
                            </div>

                            {/* NOTE: Add Items button removed because items come strictly from the contract now */}
                        </div>

                        {/* Table */}
                        <div className="flex-1 overflow-auto p-6">
                            <table className="w-full text-left border-collapse border border-black">
                                <thead>
                                    <tr>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black w-24">Codigo</th>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black">Partidas</th>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black w-48 text-right">Valor Acumulado</th>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black w-48 text-right">Monto a Pagar Acumulado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentDraftEp.items?.map(item => (
                                        <tr key={item.id} onClick={() => handleOpenEpDetail(item)} className="cursor-pointer hover:bg-blue-50">
                                            <td className="border border-black px-2 py-1 text-sm">{item.code}</td>
                                            <td className="border border-black px-2 py-1 text-sm">{item.description}</td>
                                            <td className="border border-black px-2 py-1 text-sm text-right">{item.totalValue.toLocaleString()}</td>
                                            <td className="border border-black px-2 py-1 text-sm text-right font-bold">{item.paidAmount.toLocaleString()}</td>
                                        </tr>
                                    ))}
                                    {(!currentDraftEp.items || currentDraftEp.items.length === 0) && (
                                        <tr><td colSpan={4} className="border border-black p-4 text-center text-slate-500 italic">No hay partidas añadidas.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer Buttons */}
                        <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3 bg-white dark:bg-slate-900">
                            <button onClick={() => setGenerateEpOpen(false)} className="px-6 py-2 bg-white border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 rounded">Cancelar</button>
                            <button className="px-6 py-2 bg-blue-600 text-white font-bold hover:bg-blue-700 rounded shadow-sm">Guardar</button>
                            <button onClick={handleAuthorizeClick} className={`px-6 py-2 bg-green-600 text-white font-bold hover:bg-green-700 rounded shadow-sm ${!hasPermission("sc_ep", "edit") ? "opacity-50 pointer-events-none" : ""}`}>Autorizar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 2. Detail EP Modal */}
            {detailEpOpen && activeDraftTask && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-none shadow-2xl w-full max-w-6xl border border-slate-300 dark:border-slate-700 flex flex-col max-h-[90vh] overflow-hidden">

                        {/* Blue Header */}
                        <div className="bg-blue-600 px-6 py-4">
                            <h3 className="text-lg font-bold text-white uppercase tracking-wider">DETALLE ESTADO DE PAGO</h3>
                        </div>

                        {/* Detail Table */}
                        <div className="flex-1 overflow-auto p-6">
                            <table className="w-full text-left border-collapse border border-black">
                                <thead>
                                    <tr>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black w-24">Codigo</th>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black">Partidas</th>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black">Nivel</th>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black">Unidad Fisica</th>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black text-right w-32">Valor</th>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black text-center w-40">Protocolo Aprobado</th>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black text-right w-32">Monto a Pagar</th>
                                        <th className="border border-black px-2 py-1 text-sm font-normal text-black w-10 text-center">Sel.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        // We need to list ALL units available for this task (from the item details in draft)
                                        const item = currentDraftEp.items?.find(i => i.id === activeDraftTask.id);
                                        const previousEPsForContract = paymentStatements.filter(ep => ep.contractId === currentDraftEp.contractId && ep.id !== currentDraftEp.id);

                                        return item?.details.map(detail => {
                                            // Check if already paid in PREVIOUS EPs of this contract
                                            const isAlreadyPaid = previousEPsForContract.some(pEp =>
                                                pEp.items?.some(i => i.id === activeDraftTask.id && i.selectedUnitIds?.includes(detail.unitId))
                                            );

                                            const isPayable = isProtocolPayable(activeDraftTask.id, detail.unitId);
                                            const amountToPay = isPayable ? detail.value : 0;
                                            const isSelected = tempSelectedDetailUnits.includes(detail.unitId);

                                            return (
                                                <tr key={detail.unitId} className={`hover:bg-slate-50 ${isAlreadyPaid ? 'opacity-60 bg-slate-50' : ''}`}>
                                                    <td className="border border-black px-2 py-1 text-sm">{activeDraftTask.code}</td>
                                                    <td className="border border-black px-2 py-1 text-sm">{activeDraftTask.name}</td>
                                                    <td className="border border-black px-2 py-1 text-sm">{levels.find(l => l.id === units.find(u => u.id === detail.unitId)?.levelId)?.name || "-"}</td>
                                                    <td className="border border-black px-2 py-1 text-sm">{detail.unitName}</td>
                                                    <td className="border border-black px-2 py-1 text-sm text-right">{detail.value.toLocaleString()}</td>
                                                    <td className={`border border-black px-2 py-1 text-sm text-center font-bold`}>
                                                        <div className="flex items-center justify-center gap-1">
                                                            {isAlreadyPaid ? (
                                                                <span className="text-blue-600">YA PAGADO</span>
                                                            ) : (
                                                                <>
                                                                    <span className={isPayable ? 'text-green-600' : 'text-red-500 bg-red-50'}>{isPayable ? 'SI' : 'NO'}</span>
                                                                    {!isPayable && (
                                                                        <span className="material-symbols-outlined text-[16px]" title="Protocolo Pendiente de Aprobador de Pago">warning</span>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="border border-black px-2 py-1 text-sm text-right font-bold">
                                                        {isAlreadyPaid ? '-' : (amountToPay > 0 ? amountToPay.toLocaleString() : '-')}
                                                    </td>
                                                    <td className="border border-black px-2 py-1 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={isAlreadyPaid ? true : isSelected}
                                                            disabled={isAlreadyPaid || amountToPay <= 0}
                                                            onChange={() => toggleDetailUnitSelection(detail.unitId, amountToPay)}
                                                            className="rounded border-slate-300 text-primary focus:ring-primary disabled:opacity-30 disabled:cursor-not-allowed"
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        });
                                    })()}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3 bg-white dark:bg-slate-900">
                            <button onClick={() => setDetailEpOpen(false)} className="px-6 py-2 bg-white border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 rounded">Cancelar</button>
                            <button onClick={handleSaveDetailEp} className="px-6 py-2 bg-blue-600 text-white font-bold hover:bg-blue-700 rounded shadow-sm">Guardar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 3. Signature Modal */}
            {signatureModalOpen && (
                <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-[500px] border border-slate-300 p-6">
                        <h3 className="text-lg font-bold mb-4 text-center">Autorizar Estado de Pago</h3>
                        <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 h-40 bg-slate-50 dark:bg-slate-800 rounded flex items-center justify-center cursor-crosshair relative group">
                            <span className="text-slate-400 group-hover:hidden select-none">Dibuje su firma aquí</span>
                            <div className="hidden group-hover:block absolute text-4xl text-blue-600 font-handwriting select-none" style={{ fontFamily: 'cursive' }}>Firma Autorizada</div>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <button onClick={() => setSignatureModalOpen(false)} className="flex-1 py-2 border border-slate-300 rounded text-slate-600 hover:bg-slate-50">Cancelar</button>
                            <button onClick={handleConfirmSignature} className="flex-1 py-2 bg-green-600 text-white rounded font-bold hover:bg-green-700">Confirmar Firma</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 1. Modal: Crear Master Subcontrato (DB) */}
            {createMasterSubModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[90vh]">
                        <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-slate-800 dark:text-white">Crear Nuevo Subcontrato</h3>
                            <button onClick={() => setCreateMasterSubModalOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                        </div>
                        <div className="p-6 overflow-y-auto">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">R.U.T</label>
                                    <input type="text" className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white" value={newSubcontractor.rut} onChange={(e) => setNewSubcontractor({ ...newSubcontractor, rut: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Razón Social</label>
                                    <input type="text" className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white" value={newSubcontractor.razonSocial} onChange={(e) => setNewSubcontractor({ ...newSubcontractor, razonSocial: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Giro</label>
                                    <input type="text" className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white" value={newSubcontractor.giro} onChange={(e) => setNewSubcontractor({ ...newSubcontractor, giro: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Tipo de Subcontrato</label>
                                    <select
                                        className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white"
                                        value={newSubcontractor.subcontractTypeId}
                                        onChange={(e) => setNewSubcontractor({ ...newSubcontractor, subcontractTypeId: e.target.value })}
                                    >
                                        <option value="">Seleccionar Tipo...</option>
                                        {subcontractTypes.map(t => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Representante</label>
                                    <input type="text" className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white" value={newSubcontractor.representante} onChange={(e) => setNewSubcontractor({ ...newSubcontractor, representante: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Teléfono</label>
                                    <input type="text" className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white" value={newSubcontractor.telefono} onChange={(e) => setNewSubcontractor({ ...newSubcontractor, telefono: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Fono</label>
                                    <input type="text" className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white" value={newSubcontractor.fono} onChange={(e) => setNewSubcontractor({ ...newSubcontractor, fono: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Correo</label>
                                    <input type="email" className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white" value={newSubcontractor.correo} onChange={(e) => setNewSubcontractor({ ...newSubcontractor, correo: e.target.value })} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Ciudad (Región)</label>
                                    <select
                                        className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white"
                                        value={newSubcontractor.ciudad}
                                        onChange={(e) => setNewSubcontractor({ ...newSubcontractor, ciudad: e.target.value, comuna: '' })}
                                    >
                                        <option value="">Seleccionar Región...</option>
                                        {chileData.map(d => (
                                            <option key={d.region} value={d.region}>{d.region}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Comuna</label>
                                    <select
                                        className="w-full border border-slate-300 dark:border-slate-600 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary dark:bg-slate-800 dark:text-white"
                                        value={newSubcontractor.comuna}
                                        onChange={(e) => setNewSubcontractor({ ...newSubcontractor, comuna: e.target.value })}
                                        disabled={!newSubcontractor.ciudad}
                                    >
                                        <option value="">Seleccionar Comuna...</option>
                                        {chileData.find(d => d.region === newSubcontractor.ciudad)?.comunas.map(c => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-span-1 md:col-span-2 mt-2">
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Usuarios Vinculados</label>
                                    <div className="border border-slate-300 dark:border-slate-600 rounded-lg p-2 max-h-40 overflow-y-auto bg-white dark:bg-slate-800">
                                        {globalUsers.map(user => {
                                            const isChecked = (newSubcontractor.linkedUserIds || []).includes(user.id);
                                            return (
                                                <label key={user.id} className="flex items-center gap-2 p-1 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer rounded">
                                                    <input 
                                                        type="checkbox" 
                                                        checked={isChecked}
                                                        onChange={(e) => {
                                                            const currentIds = newSubcontractor.linkedUserIds || [];
                                                            if (e.target.checked) {
                                                                setNewSubcontractor({ ...newSubcontractor, linkedUserIds: [...currentIds, user.id] });
                                                            } else {
                                                                setNewSubcontractor({ ...newSubcontractor, linkedUserIds: currentIds.filter(id => id !== user.id) });
                                                            }
                                                        }}
                                                        className="rounded border-slate-300 text-primary"
                                                    />
                                                    <span className="text-sm text-slate-700 dark:text-slate-300">{user.name} ({user.email})</span>
                                                </label>
                                            )
                                        })}
                                        {globalUsers.length === 0 && <span className="text-xs text-slate-500">No hay usuarios disponibles</span>}
                                    </div>
                                </div>

                            </div>
                        </div>
                        <div className="p-6 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3 rounded-b-xl">
                            <button onClick={() => setCreateMasterSubModalOpen(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg font-medium text-sm">Cancelar</button>
                            <button onClick={handleSaveMasterSubcontractor} className="px-6 py-2 bg-primary text-white hover:bg-blue-600 rounded-lg font-bold text-sm">Guardar Subcontrato</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 2. Modal: Lista Master Subcontratos */}
            {listMasterSubModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-5xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[85vh]">
                        <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-slate-800 dark:text-white">Lista de Subcontratos</h3>
                            <button onClick={() => setListMasterSubModalOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                        </div>
                        <div className="flex-1 overflow-auto p-0">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0 shadow-sm">
                                    <tr>
                                        <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">R.U.T</th>
                                        <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Razón Social</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700 bg-white dark:bg-slate-900">
                                    {subcontractors.map((sub) => (
                                        <tr key={sub.id}>
                                            <td className="px-6 py-4 text-sm font-mono">{sub.rut}</td>
                                            <td className="px-6 py-4 text-sm font-bold">{sub.razonSocial}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 rounded-b-xl flex justify-end">
                            <button onClick={() => setListMasterSubModalOpen(false)} className="px-6 py-2 bg-white border rounded-lg text-sm font-medium">Cerrar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 3. NEW Modal: AÑADIR SUBCONTRATOS (Create Payment Statement) */}
            {addStatementModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-0 md:p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-none md:rounded-lg shadow-2xl w-full max-w-7xl border-0 md:border border-slate-300 dark:border-slate-700 flex flex-col h-full md:h-[85vh] overflow-hidden">

                        {/* Header Blue */}
                        <div className="bg-blue-600 px-6 py-3 flex justify-between items-center shrink-0">
                            <h3 className="text-lg font-bold text-white uppercase tracking-wider">
                                {currentForm.id ? 'EDITAR SUBCONTRATO' : 'AÑADIR SUBCONTRATO'}
                                {isEditingAuthorized && <span className="ml-3 text-xs bg-white/20 px-2 py-0.5 rounded-full font-medium">Solo Lectura</span>}
                            </h3>
                            <button onClick={() => setAddStatementModalOpen(false)} className="text-white/80 hover:text-white">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto flex flex-col">
                        {/* Form Area */}
                        <div className="p-3 md:p-6 grid grid-cols-12 gap-3 md:gap-6 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shrink-0">
                            {/* RUT */}
                            <div className="col-span-5 md:col-span-2">
                                <label className="block text-[10px] md:text-xs text-slate-800 dark:text-slate-200 mb-1 font-medium">R.U.T</label>
                                <input
                                    type="text"
                                    readOnly
                                    disabled={isEditingAuthorized}
                                    className="w-full border border-slate-400 dark:border-slate-600 p-1.5 md:p-2 text-xs md:text-sm bg-slate-50 dark:bg-slate-800 disabled:opacity-70 disabled:cursor-not-allowed"
                                    value={currentForm.rut}
                                />
                            </div>

                            {/* Razon Social with Picker */}
                            <div className="col-span-7 md:col-span-4">
                                <label className="block text-[10px] md:text-xs text-slate-800 dark:text-slate-200 mb-1 font-medium uppercase">RAZÓN SOCIAL</label>
                                <div className="flex gap-1">
                                    <input
                                        type="text"
                                        readOnly
                                        disabled={isEditingAuthorized}
                                        className="w-full border border-slate-400 dark:border-slate-600 p-1.5 md:p-2 text-xs md:text-sm bg-slate-50 dark:bg-slate-800 disabled:opacity-70 disabled:cursor-not-allowed"
                                        value={currentForm.razonSocial}
                                    />
                                    <button
                                        onClick={() => !isEditingAuthorized && setSubcontractorPickerOpen(true)}
                                        disabled={isEditingAuthorized}
                                        className="bg-slate-300 hover:bg-slate-400 text-slate-700 px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm font-bold rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        ...
                                    </button>
                                </div>
                            </div>

                            {/* Usuarios Vinculados */}
                            <div className="col-span-12 md:col-span-6">
                                <label className="block text-[10px] md:text-xs text-slate-800 dark:text-slate-200 mb-1 font-medium uppercase">Usuarios Vinculados</label>
                                <div className="relative">
                                    <div className="border border-slate-400 dark:border-slate-600 p-1 md:p-1.5 text-xs md:text-sm bg-slate-50 dark:bg-slate-800 flex flex-wrap gap-1 min-h-[34px] md:min-h-[38px] items-center overflow-x-auto max-w-full relative group">
                                        {currentForm.linkedUserIds.length === 0 && (
                                            <span className="text-slate-400 italic px-1 absolute pointer-events-none whitespace-nowrap text-[10px] md:text-xs">Ninguno seleccionado</span>
                                        )}
                                        {currentForm.linkedUserIds.map(uid => {
                                            const u = (responsibles || []).find(gu => gu.id === uid) || (globalUsers || []).find(gu => gu.id === uid);
                                            return (
                                                <span key={uid} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded flex items-center gap-1 text-[10px] md:text-xs whitespace-nowrap">
                                                    {u?.name || u?.email || uid}
                                                    {!isEditingAuthorized && (
                                                        <span 
                                                            className="material-symbols-outlined text-[14px] cursor-pointer hover:text-red-500"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setCurrentForm({...currentForm, linkedUserIds: currentForm.linkedUserIds.filter(id => id !== uid)});
                                                            }}
                                                        >close</span>
                                                    )}
                                                </span>
                                            );
                                        })}
                                        {!isEditingAuthorized && (
                                            <select
                                                className="opacity-0 absolute inset-0 w-full h-full cursor-pointer z-10"
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val && !currentForm.linkedUserIds.includes(val)) {
                                                        setCurrentForm({...currentForm, linkedUserIds: [...currentForm.linkedUserIds, val]});
                                                    }
                                                    e.target.value = '';
                                                }}
                                            >
                                                <option value="">+ Añadir Usuario</option>
                                                {(responsibles || []).map(u => (
                                                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                    <div className="text-[9px] text-slate-500 mt-0.5">Seleccione usuarios que participan en la obra para vincular al contratista.</div>
                                </div>
                            </div>

                            {/* Fechas */}
                            <div className="col-span-4 md:col-span-2">
                                <label className="block text-[10px] md:text-xs text-slate-800 dark:text-slate-200 mb-1 font-medium truncate">F. Inicio</label>
                                <div className="relative">
                                    <input
                                        type="date"
                                        disabled={isEditingAuthorized}
                                        className="w-full border border-slate-400 dark:border-slate-600 p-1.5 md:p-2 text-xs md:text-sm text-slate-600 disabled:opacity-70 disabled:cursor-not-allowed"
                                        value={currentForm.startDate}
                                        onChange={e => setCurrentForm({ ...currentForm, startDate: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="col-span-4 md:col-span-2">
                                <label className="block text-[10px] md:text-xs text-slate-800 dark:text-slate-200 mb-1 font-medium truncate">F. Termino</label>
                                <div className="relative">
                                    <input
                                        type="date"
                                        disabled={isEditingAuthorized}
                                        className="w-full border border-slate-400 dark:border-slate-600 p-1.5 md:p-2 text-xs md:text-sm text-slate-600 disabled:opacity-70 disabled:cursor-not-allowed"
                                        value={currentForm.endDate}
                                        onChange={e => setCurrentForm({ ...currentForm, endDate: e.target.value })}
                                    />
                                </div>
                            </div>

                            {/* Retencion */}
                            <div className="col-span-4 md:col-span-2 text-center">
                                <label className="block text-[10px] md:text-xs text-slate-800 dark:text-slate-200 mb-1 font-medium">%Retención</label>
                                <div className="relative">
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        disabled={isEditingAuthorized}
                                        className="w-full border border-slate-400 dark:border-slate-600 p-1.5 md:p-2 text-xs md:text-sm text-center text-slate-600 focus:ring-2 focus:ring-primary outline-none disabled:opacity-70 disabled:cursor-not-allowed"
                                        value={currentForm.retention}
                                        onChange={e => setCurrentForm({ ...currentForm, retention: parseFloat(e.target.value) || 0 })}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Toolbar Area */}
                        <div className="px-3 md:px-6 py-3 md:py-4 flex flex-col md:flex-row items-stretch md:items-center justify-between bg-white dark:bg-slate-900 gap-3 md:gap-4">
                            <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 md:gap-4 w-full">
                                <button
                                    onClick={() => handleOpenTaskSelector(false)}
                                    disabled={isEditingAuthorized}
                                    className="bg-[#00b050] hover:bg-[#009040] text-white px-4 py-2 md:py-1.5 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                >
                                    +Añadir Partidas
                                </button>

                                <div className="relative flex-1 w-full md:max-w-lg">
                                    <input
                                        type="text"
                                        className="w-full border border-slate-400 p-1.5 pl-2 text-sm"
                                    />
                                    <span className="material-symbols-outlined absolute right-2 top-1.5 text-slate-400 text-[18px]">search</span>
                                </div>
                            </div>

                            <div className="relative w-full md:w-auto md:ml-4 shrink-0 flex items-center">
                                <button 
                                    type="button"
                                    onClick={() => setDocumentsModalOpen(true)}
                                    className={`bg-[#ffc000] hover:bg-[#e0a800] text-black px-4 py-2 md:py-1.5 text-sm font-medium transition-colors w-full h-full whitespace-nowrap rounded flex items-center justify-center gap-1 ${(currentForm.attachedDocuments?.length || 0) > 0 ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
                                >
                                    <span className="material-symbols-outlined text-[18px]">attach_file</span>
                                    Documentos del Contrato ({(currentForm.attachedDocuments || []).length})
                                </button>
                            </div>
                        </div>

                        {/* Table Area */}
                        <div className="flex-none overflow-x-auto px-2 md:px-6 pb-6">
                            <div className="min-w-max">
                                <table className="w-full border border-black text-left border-collapse">
                                <thead>
                                    <tr className="bg-white">
                                        <th className="border border-black px-1 md:px-2 py-1 text-[10px] md:text-xs font-normal text-black w-8 md:w-10 text-center"></th>
                                        <th className="border border-black px-1 md:px-2 py-1 text-[10px] md:text-xs font-normal text-black w-16 md:w-24">Codigo</th>
                                        <th className="border border-black px-1 md:px-2 py-1 text-[10px] md:text-xs font-normal text-black">Partidas</th>
                                        <th className="border border-black px-1 md:px-2 py-1 text-[10px] md:text-xs font-normal text-black w-12 md:w-20 text-center">Unidad</th>
                                        <th className="border border-black px-1 md:px-2 py-1 text-[10px] md:text-xs font-normal text-black w-16 md:w-24 text-center">Total Cant.</th>
                                        <th className="border border-black px-1 md:px-2 py-1 text-[10px] md:text-xs font-normal text-black w-24 md:w-40 text-right">Valor Acumulado</th>
                                        <th className="border border-black px-1 md:px-2 py-1 text-[10px] md:text-xs font-normal text-black w-24 md:w-40 text-right">Monto Pagado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentForm.items.length === 0 ? (
                                        <tr><td colSpan={5} className="border border-black px-2 py-8 text-center text-xs text-slate-500">No hay partidas añadidas.</td></tr>
                                    ) : (
                                        currentForm.items.map((item) => (
                                            <React.Fragment key={item.id}>
                                                <tr className="bg-white hover:bg-slate-50">
                                                    <td className="border border-black px-2 py-1 text-center">
                                                        <button
                                                            onClick={() => toggleItemExpansion(item.id)}
                                                            className="text-slate-500 hover:text-black flex items-center justify-center"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">
                                                                {item.expanded ? 'expand_less' : 'expand_more'}
                                                            </span>
                                                        </button>
                                                    </td>
                                                    <td className="border border-black px-2 py-1 text-xs">{item.code}</td>
                                                    <td className="border border-black px-2 py-1 text-xs font-bold">{item.description}</td>
                                                    <td className="border border-black px-2 py-1 text-xs text-center">{findTaskById(item.id, tasks)?.unit || '-'}</td>
                                                    <td className="border border-black px-2 py-1 text-xs text-center font-bold">
                                                        {item.details.reduce((sum, d) => sum + (parseFloat(d.quantity || '0') || 0), 0).toLocaleString()}
                                                    </td>
                                                    <td className="border border-black px-2 py-1 text-xs text-right font-medium">{item.totalValue.toLocaleString()}</td>
                                                    <td className="border border-black px-2 py-1 text-xs text-right">{item.paidAmount || ''}</td>
                                                </tr>
                                                {item.expanded && (
                                                    <tr>
                                                        <td colSpan={7} className="border border-black p-0">
                                                            <div className="bg-slate-50 p-2">
                                                                <table className="w-full text-left bg-white border border-slate-200">
                                                                    <thead className="bg-slate-100 text-xs text-slate-500">
                                                                        <tr>
                                                                            <th className="p-2 border-b">Nivel</th>
                                                                            <th className="p-2 border-b">Unidad Física</th>
                                                                            <th className="p-2 border-b text-center">Cantidad</th>
                                                                            <th className="p-2 border-b text-right">Valor Unitario</th>
                                                                            <th className="p-2 border-b text-right">Valor</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {item.details.map((detail, dIdx) => (
                                                                            <tr key={dIdx} className="border-b border-slate-100 last:border-0 text-xs text-slate-600">
                                                                                <td className="p-2">{levels.find(l => l.id === units.find(u => u.id === detail.unitId)?.levelId)?.name || "-"}</td>
                                                                                <td className="p-2">{detail.unitName}</td>
                                                                                <td className="p-2 text-center">{detail.quantity || '0'}</td>
                                                                                <td className="p-2 text-right font-mono">{detail.unitPrice ? `$${parseInt(detail.unitPrice).toLocaleString()}` : '-'}</td>
                                                                                <td className="p-2 text-right font-mono">${detail.value.toLocaleString()}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))
                                    )}
                                </tbody>
                                </table>
                            </div>
                        </div>

                        </div>

                        {/* Footer Actions for the Modal */}
                        <div className="p-4 bg-slate-100 dark:bg-slate-800 flex justify-end gap-3 border-t border-slate-300">
                            {/* Autorizar button removed based on user request */}
                            <button onClick={() => setAddStatementModalOpen(false)} className="px-4 py-2 bg-white border border-slate-400 text-sm font-medium hover:bg-slate-50">Cancelar</button>
                            {!isEditingAuthorized && (() => {
                                const isContractValid = Boolean(currentForm.subcontractorId && currentForm.items.length > 0 && currentForm.rut && currentForm.razonSocial && currentForm.startDate && currentForm.endDate && currentForm.retention !== undefined && ((currentForm as any).signedContractUrl || (currentForm.attachedDocuments && currentForm.attachedDocuments.length > 0)));
                                const isDraft = currentForm.subcontractorId || currentForm.items.length > 0;
                                
                                if (isContractValid) {
                                    return (
                                        <button onClick={handleSaveContract} className="px-6 py-2 bg-blue-600 text-white text-sm font-bold hover:bg-blue-700">{currentForm.id ? 'Guardar Cambios' : 'Habilitar Contratista'}</button>
                                    );
                                } else if (isDraft) {
                                    return (
                                        <button onClick={handleSaveContract} className="px-6 py-2 bg-amber-500 text-white text-sm font-bold hover:bg-amber-600">Guardado Campos Ingresados</button>
                                    );
                                }
                                return (
                                    <button disabled className="px-6 py-2 bg-slate-300 text-white text-sm font-bold cursor-not-allowed">{currentForm.id ? 'Guardar Cambios' : 'Habilitar Contratista'}</button>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* Picker: Select Subcontractor for the Form */}
            {subcontractorPickerOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded shadow-xl w-[500px] border border-slate-300 p-4">
                        <h3 className="font-bold mb-4 text-sm uppercase">Seleccionar Subcontratista</h3>
                        <div className="max-h-[300px] overflow-y-auto border border-slate-200 mb-4">
                            {subcontractors.map(sub => (
                                <div
                                    key={sub.id}
                                    onClick={() => handlePickSubcontractor(sub)}
                                    className="p-2 hover:bg-blue-50 cursor-pointer border-b border-slate-100 text-sm"
                                >
                                    <div className="font-bold">{sub.razonSocial}</div>
                                    <div className="text-xs text-slate-500">{sub.rut}</div>
                                </div>
                            ))}
                        </div>
                        <button onClick={() => setSubcontractorPickerOpen(false)} className="w-full border py-1 text-sm bg-slate-100 hover:bg-slate-200">Cancelar</button>
                    </div>
                </div>
            )}

            {/* STEP 1: Task Selector (List of Budget Items) */}
            {taskSelectorOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 rounded shadow-xl w-full max-w-2xl border border-slate-300 p-4 max-h-[85vh] flex flex-col">
                        <h3 className="font-bold mb-4 text-sm uppercase flex items-center gap-2 shrink-0">
                            <span className="material-symbols-outlined text-[18px]">list_alt</span>
                            Seleccionar Partida
                        </h3>
                        <div className="flex-1 overflow-auto border border-slate-200 mb-4 rounded bg-white min-h-[300px]">
                            <div className="min-w-max">
                                {renderTaskSelectorItems(tasks)}
                            </div>
                        </div>
                        <button onClick={() => setTaskSelectorOpen(false)} className="w-full border py-2 text-sm font-bold bg-slate-100 hover:bg-slate-200 rounded shrink-0 transition-colors">Cancelar</button>
                    </div>
                </div>
            )}

            {/* STEP 2: Unit Selector (The Requested Image) */}
            {unitSelectorOpen && selectedTaskForAdd && (() => {
                const validUnits = getValidUnitsForTask(selectedTaskForAdd.id);
                const totalSelected = tempSelectedUnits.reduce((acc, uid) => {
                    const u = validUnits.find(v => v.id === uid);
                    return acc + (u?.value || 0);
                }, 0);
                const allSelected = validUnits.length > 0 && validUnits.every(u => tempSelectedUnits.includes(u.id));
                const someSelected = validUnits.some(u => tempSelectedUnits.includes(u.id)) && !allSelected;

                const handleToggleAll = () => {
                    if (allSelected) {
                        setTempSelectedUnits([]);
                    } else {
                        setTempSelectedUnits(validUnits.map(u => u.id));
                    }
                };

                return (
                    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-0 md:p-4">
                        <div className="bg-white dark:bg-slate-900 rounded-none md:rounded-lg shadow-xl w-full max-w-3xl border-0 md:border border-slate-300 flex flex-col h-full md:h-auto md:max-h-[80vh]">
                            <div className="p-3 md:p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 shrink-0">
                                <div>
                                    <h3 className="font-bold text-xs md:text-sm uppercase text-slate-700">Partida: {selectedTaskForAdd.name}</h3>
                                    <p className="text-[10px] md:text-xs text-slate-500">Código: {selectedTaskForAdd.code}</p>
                                </div>
                                <button onClick={() => setUnitSelectorOpen(false)} className="text-slate-400 hover:text-black"><span className="material-symbols-outlined">close</span></button>
                            </div>

                            {/* Select All Bar */}
                            {validUnits.length > 0 && (
                                <div className="px-3 md:px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2 md:gap-3 shrink-0">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        ref={el => { if (el) el.indeterminate = someSelected; }}
                                        onChange={handleToggleAll}
                                        className="rounded border-slate-300 text-primary focus:ring-primary size-4 cursor-pointer"
                                        id="select-all-units"
                                    />
                                    <label htmlFor="select-all-units" className="text-[10px] md:text-xs font-semibold text-blue-700 cursor-pointer select-none">
                                        {allSelected ? 'Deseleccionar Todo' : 'Seleccionar Todo'}
                                        <span className="ml-1 md:ml-2 text-blue-400 font-normal">({tempSelectedUnits.length}/{validUnits.length} seleccionadas)</span>
                                    </label>
                                </div>
                            )}

                            <div className="flex-1 overflow-x-auto overflow-y-auto p-0">
                                <div className="min-w-max">
                                    <table className="w-full text-left border-collapse">
                                        <thead className="bg-white sticky top-0 shadow-sm z-10">
                                        <tr>
                                            <th className="p-1.5 md:p-3 border-b text-[10px] md:text-xs font-bold w-8 md:w-12 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={allSelected}
                                                    onChange={handleToggleAll}
                                                    className="rounded border-slate-300 text-primary focus:ring-primary size-3 md:size-4 cursor-pointer"
                                                />
                                            </th>
                                            <th className="p-1.5 md:p-3 border-b text-[10px] md:text-xs font-bold text-slate-500 uppercase">Nivel</th>
                                            <th className="p-1.5 md:p-3 border-b text-[10px] md:text-xs font-bold text-slate-500 uppercase">Unidad Física</th>
                                            <th className="p-1.5 md:p-3 border-b text-[10px] md:text-xs font-bold text-center text-slate-500 uppercase">Cantidad</th>
                                            <th className="p-1.5 md:p-3 border-b text-[10px] md:text-xs font-bold text-right text-slate-500 uppercase">Valor Unit.</th>
                                            <th className="p-1.5 md:p-3 border-b text-[10px] md:text-xs font-bold text-right text-slate-500 uppercase">Valor</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {validUnits.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="p-8 text-center">
                                                    <div className="flex flex-col items-center gap-2 text-slate-500">
                                                        <span className="material-symbols-outlined text-4xl text-slate-300">money_off</span>
                                                        <span className="text-sm font-medium">No se han asociado presupuestos</span>
                                                        <p className="text-xs max-w-xs">Debe ingresar valores en la columna "Estados de Pago" del módulo EDT para esta partida.</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            validUnits.map(unit => {
                                                const levelName = levels.find(l => l.id === unit.levelId)?.name || '-';
                                                const alreadyInAnotherContract = isUnitAlreadyContracted(unit.id, selectedTaskForAdd.id, currentForm.id);
                                                return (
                                                    <tr key={unit.id} className={`hover:bg-blue-50 transition-colors ${tempSelectedUnits.includes(unit.id) ? 'bg-blue-50/50' : ''} ${alreadyInAnotherContract ? 'opacity-50 bg-slate-100' : ''}`}>
                                                        <td className="p-1.5 md:p-3 border-b text-center relative">
                                                            <input
                                                                type="checkbox"
                                                                checked={alreadyInAnotherContract || tempSelectedUnits.includes(unit.id)}
                                                                onChange={() => toggleUnitSelection(unit.id)}
                                                                disabled={alreadyInAnotherContract}
                                                                className="rounded border-slate-300 text-primary focus:ring-primary size-3 md:size-4 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                            />
                                                            {alreadyInAnotherContract && (
                                                                <span className="absolute left-1/2 -translate-x-1/2 bottom-0.5 md:-bottom-2 text-[6px] md:text-[8px] font-bold text-red-500 uppercase tracking-tighter whitespace-nowrap pointer-events-none">Asignado</span>
                                                            )}
                                                        </td>
                                                        <td className="p-1.5 md:p-3 border-b text-[10px] md:text-sm text-slate-500">{levelName}</td>
                                                        <td className="p-1.5 md:p-3 border-b text-[10px] md:text-sm font-medium">
                                                            {unit.name}
                                                        </td>
                                                        <td className="p-1.5 md:p-3 border-b text-[10px] md:text-sm text-center">{unit.quantity}</td>
                                                        <td className="p-1.5 md:p-3 border-b text-[10px] md:text-sm text-right font-mono">{unit.unitPrice ? `$${parseInt(unit.unitPrice).toLocaleString()}` : '-'}</td>
                                                        <td className="p-1.5 md:p-3 border-b text-[10px] md:text-sm text-right font-mono">${unit.value.toLocaleString()}</td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                                </div>
                            </div>

                            <div className="p-3 md:p-4 bg-slate-50 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-2 md:gap-0 shrink-0">
                                <div className="text-[10px] md:text-sm w-full md:w-auto text-center md:text-left">
                                    <span className="text-slate-500 mr-2">Total Seleccionado:</span>
                                    <span className="font-bold font-mono text-base md:text-lg">${totalSelected.toLocaleString()}</span>
                                </div>
                                <div className="flex gap-2 w-full md:w-auto">
                                    <button onClick={() => setUnitSelectorOpen(false)} className="flex-1 md:flex-none px-3 py-1.5 text-[10px] md:text-sm bg-white border border-slate-300 rounded hover:bg-slate-100">Cancelar</button>
                                    <button
                                        onClick={handleConfirmUnits}
                                        disabled={validUnits.length === 0}
                                        className={`flex-1 md:flex-none px-4 py-1.5 text-[10px] md:text-sm rounded font-bold shadow-sm ${validUnits.length === 0 ? 'bg-slate-300 text-white cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'}`}
                                    >
                                        Guardar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}



            {/* --- ANEXOS MODALS --- */}
            
            {/* Anexos List Modal */}
            {anexosListModalOpen && selectedContract && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-full max-w-5xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[80vh]">
                        <div className="bg-blue-600 p-4 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-white uppercase tracking-wider">
                                ANEXOS DE CONTRATO - {getSubcontractorById(selectedContract.subcontractorId)?.razonSocial}
                            </h3>
                            <button onClick={() => setAnexosListModalOpen(false)} className="text-white/80 hover:text-white">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="p-6 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 flex justify-between items-center">
                            <div>
                                <span className="font-bold text-slate-700">Contrato Base:</span> <span className="font-mono text-slate-600">${selectedContract.totalAmount.toLocaleString()}</span>
                            </div>
                            {hasPermission('sc_ep', 'edit') && (
                                <button
                                    onClick={handleCreateAnexo}
                                    className="bg-primary text-white px-4 py-2 rounded font-bold shadow-sm hover:bg-blue-600 flex items-center gap-2 text-sm"
                                >
                                    <span className="material-symbols-outlined text-[18px]">add</span>
                                    Nuevo Anexo
                                </button>
                            )}
                        </div>

                        <div className="flex-1 overflow-auto p-6 bg-white dark:bg-slate-900">
                            {!selectedContract.anexos || selectedContract.anexos.length === 0 ? (
                                <div className="text-center py-12 text-slate-500">
                                    <span className="material-symbols-outlined text-5xl opacity-30 mb-2">post_add</span>
                                    <p className="text-lg">No hay anexos registrados para este contrato.</p>
                                </div>
                            ) : (
                                <table className="w-full text-left border-collapse">
                                    <thead className="bg-slate-50 border-b border-slate-200">
                                        <tr>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Número</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Fecha</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase">Descripción</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase text-right">Monto Adicional</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase text-center">Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {selectedContract.anexos.map(anexo => (
                                            <tr key={anexo.id} onClick={() => handleEditAnexo(anexo)} className="cursor-pointer hover:bg-slate-50 transition-colors">
                                                <td className="px-4 py-3 font-bold text-slate-800 text-sm">{anexo.numero}</td>
                                                <td className="px-4 py-3 text-sm text-slate-600">{anexo.fecha}</td>
                                                <td className="px-4 py-3 text-sm text-slate-600">{anexo.description || '-'}</td>
                                                <td className="px-4 py-3 font-mono font-bold text-right text-slate-800 text-sm">${anexo.totalAmount.toLocaleString()}</td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide
                                                        ${anexo.status === 'Borrador' ? 'bg-blue-100 text-blue-700' :
                                                            anexo.status === 'Anulado' ? 'bg-red-100 text-red-700' :
                                                                'bg-green-100 text-green-700'}
                                                    `}>{anexo.status}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Anexo Form Modal (Create/Edit) */}
            {anexoFormModalOpen && selectedContract && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-none shadow-2xl w-full max-w-7xl border border-slate-300 dark:border-slate-700 flex flex-col h-[80vh] overflow-hidden">
                        <div className="bg-blue-600 px-6 py-3 flex justify-between items-center shrink-0">
                            <h3 className="text-lg font-bold text-white uppercase tracking-wider">
                                {currentAnexoForm.id ? 'EDITAR ANEXO' : 'AÑADIR ANEXO'}
                                {currentAnexoForm.status === 'Activo' && <span className="ml-3 text-xs bg-white/20 px-2 py-0.5 rounded-full font-medium">Solo Lectura</span>}
                            </h3>
                            <button onClick={() => setAnexoFormModalOpen(false)} className="text-white/80 hover:text-white">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="p-6 grid grid-cols-12 gap-6 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shrink-0">
                            <div className="col-span-12 md:col-span-3">
                                <label className="block text-xs text-slate-800 mb-1 font-medium">Número de Anexo</label>
                                <input
                                    type="text"
                                    disabled={currentAnexoForm.status === 'Activo'}
                                    className="w-full border border-slate-400 p-1.5 text-sm bg-white disabled:bg-slate-100"
                                    value={currentAnexoForm.numero}
                                    onChange={e => setCurrentAnexoForm({ ...currentAnexoForm, numero: e.target.value })}
                                />
                            </div>
                            <div className="col-span-12 md:col-span-3">
                                <label className="block text-xs text-slate-800 mb-1 font-medium">Fecha Aprobación</label>
                                <input
                                    type="date"
                                    disabled={currentAnexoForm.status === 'Activo'}
                                    className="w-full border border-slate-400 p-1.5 text-sm bg-white disabled:bg-slate-100"
                                    value={currentAnexoForm.fecha}
                                    onChange={e => setCurrentAnexoForm({ ...currentAnexoForm, fecha: e.target.value })}
                                />
                            </div>
                            <div className="col-span-12 md:col-span-6">
                                <label className="block text-xs text-slate-800 mb-1 font-medium">Descripción breve</label>
                                <input
                                    type="text"
                                    disabled={currentAnexoForm.status === 'Activo'}
                                    className="w-full border border-slate-400 p-1.5 text-sm bg-white disabled:bg-slate-100"
                                    placeholder="Ej: Obras extraordinarias por cambio de trazado"
                                    value={currentAnexoForm.description}
                                    onChange={e => setCurrentAnexoForm({ ...currentAnexoForm, description: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="px-6 py-4 flex items-center justify-between bg-white dark:bg-slate-900">
                            <button
                                onClick={() => handleOpenTaskSelector(true)}
                                disabled={currentAnexoForm.status === 'Activo'}
                                className="bg-[#00b050] hover:bg-[#009040] text-white px-4 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                +Añadir Partidas al Anexo
                            </button>
                        </div>

                        <div className="flex-1 overflow-auto px-6 pb-6">
                            <table className="w-full border border-black text-left border-collapse">
                                <thead>
                                    <tr className="bg-white">
                                        <th className="border border-black px-2 py-1 text-xs font-normal text-black w-10 text-center"></th>
                                        <th className="border border-black px-2 py-1 text-xs font-normal text-black w-24">Codigo</th>
                                        <th className="border border-black px-2 py-1 text-xs font-normal text-black">Partidas</th>
                                        <th className="border border-black px-2 py-1 text-xs font-normal text-black w-20 text-center">Unidad</th>
                                        <th className="border border-black px-2 py-1 text-xs font-normal text-black w-24 text-center">Cant. Ajuste</th>
                                        <th className="border border-black px-2 py-1 text-xs font-normal text-black w-40 text-right">Valor Ajuste</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* --- ORIGINAL CONTRACT ITEMS (FOR ELIMINATION/REDUCTION) --- */}
                                    {selectedContract.items.map(baseItem => {
                                        // Check if this item has an adjustment in the current anexo
                                        const adjustmentItem = currentAnexoForm.items?.find(i => i.id === baseItem.id);
                                        const isEliminated = currentAnexoForm.items?.some(i => i.id === baseItem.id && i.totalValue < 0);
                                        
                                        // Check if EDT has a lower price now (Cost Reduction Detection)
                                        const currentValidUnits = getValidUnitsForTask(baseItem.id);
                                        let totalEdtValue = 0;
                                        baseItem.details.forEach(d => {
                                            const edtUnit = currentValidUnits.find(u => u.id === d.unitId);
                                            if (edtUnit) totalEdtValue += edtUnit.value;
                                            else totalEdtValue += d.value; // Snapshot fallback
                                        });
                                        
                                        const priceDiff = totalEdtValue - baseItem.totalValue;
                                        
                                        return (
                                            <React.Fragment key={`base-${baseItem.id}`}>
                                                <tr className={`bg-slate-50 ${isEliminated ? 'opacity-50' : ''}`}>
                                                    <td className="border border-black px-2 py-1 text-center">
                                                        <button 
                                                            onClick={() => {
                                                                if (isEliminated) {
                                                                    // Restore (Remove negative adjustment)
                                                                    setCurrentAnexoForm(prev => ({
                                                                        ...prev,
                                                                        items: prev.items?.filter(i => i.id !== baseItem.id)
                                                                    }));
                                                                } else {
                                                                    // Eliminate (Add negative adjustment)
                                                                    const negItem: StatementItem = {
                                                                        ...baseItem,
                                                                        totalValue: -baseItem.totalValue,
                                                                        details: baseItem.details.map(d => ({ ...d, value: -d.value, quantity: `-${d.quantity}` })),
                                                                        expanded: false
                                                                    };
                                                                    setCurrentAnexoForm(prev => ({
                                                                        ...prev,
                                                                        items: [...(prev.items || []), negItem]
                                                                    }));
                                                                }
                                                            }}
                                                            title={isEliminated ? "Restaurar Partida" : "Eliminar de Contrato"}
                                                            className={`text-white px-2 rounded text-[10px] uppercase font-bold ${isEliminated ? 'bg-blue-500' : 'bg-red-500'}`}
                                                        >
                                                            {isEliminated ? 'Restaurar' : 'Eliminar'}
                                                        </button>
                                                    </td>
                                                    <td className="border border-black px-2 py-1 text-xs text-slate-400 italic">{baseItem.code}</td>
                                                    <td className="border border-black px-2 py-1 text-xs">
                                                        <div className="flex flex-col">
                                                            <span className="font-bold text-slate-500">{baseItem.description} (Original)</span>
                                                            {priceDiff < 0 && (
                                                                <span className="text-[9px] text-red-600 font-bold bg-red-50 px-1 rounded self-start mt-0.5 animate-pulse">
                                                                    Diferencia EDT vs Contrato: ${priceDiff.toLocaleString()} CLP
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="border border-black px-2 py-1 text-xs text-center text-slate-400">{findTaskById(baseItem.id, tasks)?.unit || '-'}</td>
                                                    <td className="border border-black px-2 py-1 text-xs text-center font-bold">
                                                        {isEliminated ? 'ELIMINADA' : '-'}
                                                    </td>
                                                    <td className={`border border-black px-2 py-1 text-xs text-right font-bold ${isEliminated ? 'text-red-600' : 'text-slate-400'}`}>
                                                        {isEliminated ? `-${baseItem.totalValue.toLocaleString()}` : '0'}
                                                    </td>
                                                </tr>
                                            </React.Fragment>
                                        );
                                    })}

                                    {/* --- NEW/ADDED ITEMS IN THIS ANEXO --- */}
                                    {currentAnexoForm.items?.filter(i => !selectedContract.items.some(bi => bi.id === i.id)).length === 0 && selectedContract.items.length === 0 && (
                                        <tr><td colSpan={6} className="border border-black px-2 py-8 text-center text-xs text-slate-500">No hay partidas en este anexo.</td></tr>
                                    )}
                                    {currentAnexoForm.items?.filter(i => !selectedContract.items.some(bi => bi.id === i.id)).map((item) => (
                                        <React.Fragment key={`added-${item.id}`}>
                                            <tr className="bg-white hover:bg-slate-50">
                                                    <td className="border border-black px-2 py-1 text-center">
                                                        <button
                                                            onClick={() => toggleItemExpansion(item.id, true)}
                                                            className="text-slate-500 hover:text-black flex items-center justify-center"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">
                                                                {item.expanded ? 'expand_less' : 'expand_more'}
                                                            </span>
                                                        </button>
                                                    </td>
                                                    <td className="border border-black px-2 py-1 text-xs">{item.code}</td>
                                                    <td className="border border-black px-2 py-1 text-xs font-bold">{item.description}</td>
                                                    <td className="border border-black px-2 py-1 text-xs text-center">{findTaskById(item.id, tasks)?.unit || '-'}</td>
                                                    <td className={`border border-black px-2 py-1 text-xs text-center font-bold ${item.totalValue < 0 ? 'text-red-700' : 'text-green-700'}`}>
                                                        {item.totalValue < 0 ? '-' : '+'}
                                                        {Math.abs(item.details.reduce((sum, d) => sum + (parseFloat(d.quantity || '0') || 0), 0)).toLocaleString()}
                                                    </td>
                                                    <td className={`border border-black px-2 py-1 text-xs text-right font-medium ${item.totalValue < 0 ? 'text-red-700' : 'text-green-700'}`}>
                                                        {item.totalValue < 0 ? '-' : '+'}
                                                        {Math.abs(item.totalValue).toLocaleString()}
                                                    </td>
                                                </tr>
                                                {item.expanded && (
                                                    <tr>
                                                        <td colSpan={6} className="border border-black p-0">
                                                            <div className="bg-slate-50 p-2">
                                                                <table className="w-full text-left bg-white border border-slate-200">
                                                                    <thead className="bg-slate-100 text-xs text-slate-500">
                                                                        <tr>
                                                                            <th className="p-2 border-b">Nivel</th>
                                                                            <th className="p-2 border-b">Unidad Física</th>
                                                                            <th className="p-2 border-b text-center">Cantidad a Agregar</th>
                                                                            <th className="p-2 border-b text-right">Valor Unitario</th>
                                                                            <th className="p-2 border-b text-right">Valor Aumento</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {item.details.map((detail, dIdx) => (
                                                                            <tr key={dIdx} className="border-b border-slate-100 last:border-0 text-xs text-slate-600">
                                                                                <td className="p-2">{levels.find(l => l.id === units.find(u => u.id === detail.unitId)?.levelId)?.name || "-"}</td>
                                                                                <td className="p-2">{detail.unitName}</td>
                                                                                <td className="p-2 text-center text-green-600 font-bold">+{detail.quantity || '0'}</td>
                                                                                <td className="p-2 text-right font-mono">{detail.unitPrice ? `$${parseInt(detail.unitPrice).toLocaleString()}` : '-'}</td>
                                                                                <td className="p-2 text-right font-mono font-bold text-green-600">+${detail.value.toLocaleString()}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                            </table>
                        </div>

                        <div className="p-4 bg-slate-100 flex justify-end gap-3 border-t border-slate-300">
                            {currentAnexoForm.status !== 'Activo' && currentAnexoForm.id && (
                                <button onClick={handleAuthorizeAnexo} className="px-6 py-2 bg-green-600 text-white text-sm font-bold hover:bg-green-700 mr-auto shadow-sm">Autorizar Anexo</button>
                            )}
                            <button onClick={() => setAnexoFormModalOpen(false)} className="px-4 py-2 bg-white border border-slate-400 text-sm font-medium hover:bg-slate-50">Cancelar</button>
                            {currentAnexoForm.status !== 'Activo' && (
                                <button onClick={handleSaveAnexo} className="px-6 py-2 bg-blue-600 text-white text-sm font-bold hover:bg-blue-700">Guardar Anexo</button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {subcontractTypeModalOpen && renderSubcontractTypeModal()}
            {epDetailModalOpen && renderEPDetailModal()}
            {/* Documents Modal */}
            {documentsModalOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-lg border border-slate-300 dark:border-slate-700 overflow-hidden flex flex-col">
                        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50">
                            <h3 className="font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                <span className="material-symbols-outlined text-blue-500">description</span>
                                Documentos del Contrato
                            </h3>
                            <button onClick={() => setDocumentsModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div className="p-4 overflow-y-auto max-h-[60vh]">
                            {(!currentForm.attachedDocuments || currentForm.attachedDocuments.length === 0) ? (
                                <div className="text-center py-8 text-slate-400 italic">No hay documentos adjuntos.</div>
                            ) : (
                                <ul className="space-y-2">
                                    {currentForm.attachedDocuments.map((doc, idx) => (
                                        <li key={idx} className="flex justify-between items-center bg-slate-50 dark:bg-slate-800 p-3 rounded border border-slate-200 dark:border-slate-700">
                                            <div className="flex items-center gap-3 overflow-hidden">
                                                <span className="material-symbols-outlined text-slate-400">insert_drive_file</span>
                                                <span className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">{doc}</span>
                                            </div>
                                            {!isEditingAuthorized && (
                                                <button 
                                                    title="Eliminar documento"
                                                    className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 p-1.5 rounded transition-colors"
                                                    onClick={() => {
                                                        const newDocs = [...currentForm.attachedDocuments];
                                                        newDocs.splice(idx, 1);
                                                        setCurrentForm({ ...currentForm, attachedDocuments: newDocs });
                                                    }}
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">delete</span>
                                                </button>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            
                            {!isEditingAuthorized && (
                                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                                    <div className="relative overflow-hidden rounded bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 border border-slate-300 dark:border-slate-600 border-dashed w-full">
                                        <input 
                                            type="file" 
                                            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10" 
                                            title="Agregar más archivos..." 
                                            onChange={(e) => {
                                                if (e.target.files && e.target.files.length > 0) {
                                                    setCurrentForm({ 
                                                        ...currentForm, 
                                                        attachedDocuments: [...(currentForm.attachedDocuments || []), e.target.files[0].name] 
                                                    });
                                                }
                                            }}
                                        />
                                        <div className="flex items-center justify-center gap-2 py-3 text-sm font-medium text-slate-600 dark:text-slate-400 cursor-pointer">
                                            <span className="material-symbols-outlined text-[20px]">add_circle</span>
                                            Adjuntar archivo
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="p-4 bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
                            <button onClick={() => setDocumentsModalOpen(false)} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded shadow-sm transition-colors">
                                Guardar y Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentsScreen;
